/* eslint-disable import/exports-last */
import { createError } from "../errors/errors";
import BarrelType from "../model/BarrelType";
import NoteStatus from "../model/NoteStatus";
import { NoteTypeData } from "../model/NoteType";
import { RecursivePartial, RequiredOnly } from "../utils/UtilityTypes";
import {
    BASE_URL,
    GOOGLE_AUTH_SETUP_SSO_REDIRECT_URI,
    GOOGLE_AUTH_REMOVE_SSO_REDIRECT_URI
} from "../constants";
import { TagGroupOrderBy } from "../enums";

import {
    handleResponse,
    checkResponseError,
    checkResponseErrorWithRedirect,
    handleResponseWithRedirect,
    handleResponseSilently,
    handleResponseBlob,
    refreshToken
} from "./apiUtils";
import http from "./http";
import * as Types from "./model";
import AIReviewDotRating from "./model/AIReview/AIReviewDotRating";
import AIReviewFeedback from "./model/AIReview/AIReviewFeedback";
import { GetApprovalsResponse } from "./model/approvals/Approval";
import BarrelDataStage from "./model/barrels/BarrelDataStage";
import RawColor from "./model/colors/RawColor";
import ComponentPage from "./model/components/ComponentPage";
import ComponentSection from "./model/components/ComponentSection";
import ConnectedComponent from "./model/connectedComponents/ConnectedComponent";
import FlowNodeType from "./model/flow/FlowNodeType";
import GlobalSearchSortType from "./model/globalSearch/GlobalSearchSortType";
import FigmaIntegration from "./model/integrations/barrel/FigmaIntegration";
import MFAAvailability from "./model/mfa/MFAAvailability";
import Asset from "./model/snapshots/Asset";
import { StorybookIndexResponse } from "./model/storybook/StoryIndexResponse";
import { CreatePersonalPlanParams } from "./model/stripe/CreatePersonalPlanParams";
import { UpdatePersonalPlanParams } from "./model/stripe/UpdatePersonalPlanParams";
import { VariableCollection, VariableGroup } from "./model/variables/Variables";

/**
 * Generates a shortlink for given URL, under provided pathname.
 * @param {String} oid organization id
 */
export function generateShortlink(url: string, path?: string): Promise<string> {
    const params = path ? `?path=${path}` : "";

    return http.post(`${Zeplin.urls.baseURL}/urls${params}`, { url })
        .then<{ url: string; }>(handleResponse)
        .then(({ url: shortlink }) => shortlink)
        .catch(() => url);
}

type CreateCredentialsParameters = { projects?: string; styleguides?: string; };
type CreateCredentialsResponse = {
    accessKeyId: string;
    secretAccessKey: string;
    sessionToken: string;
    expiration: string;
};
export function createTemporaryS3Credentials(
    { projects, styleguides }: CreateCredentialsParameters = {}
): Promise<CreateCredentialsResponse> {
    return http.post(`${Zeplin.urls.baseURL}/export/credentials`, {
        actions: ["GetObject", "PutObject"],
        projects,
        styleguides
    }).then<CreateCredentialsResponse>(handleResponse);
}

export function getLongURL(shortLink: string): Promise<string> {
    const headers = new Headers();
    headers.set("Accept", "application/json");

    return fetch(shortLink, {
        method: "GET",
        headers
    }).then<{url: string;}>(handleResponse)
        .then(({ url }) => url);
}

// MARK: Organization endpoints

/**
 * Create a note type for the organization
 */
export function createOrganizationNoteType(oid: string, noteType: NoteTypeData):
    Promise<Types.CreateNoteTypeResponse> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/noteTypes`, {
        name: noteType.name,
        color: noteType.color
    }).then<Types.CreateNoteTypeResponse>(handleResponse);
}

export function updateOrganizationNoteType(oid: string, ntid: string, update: Partial<NoteTypeData>): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/noteTypes/${ntid}`, update)
        .then(checkResponseError);
}

export function updateOrganizationNoteTypeOrder(oid: string, ntid: string, index: number): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/noteTypes/${ntid}/order`, { index })
        .then(checkResponseError);
}

export function removeOrganizationNoteType(oid: string, ntid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/organizations/${oid}/noteTypes/${ntid}`)
        .then(checkResponseError);
}

/**
 * Get organization list of the user.
 */
export function getOrganizations(): Promise<Types.Organization[]> {
    return http
        .get(`${Zeplin.urls.baseURL}/v2/organizations`)
        .then<{ organizations: Types.Organization[]; }>(handleResponse)
        .then(({ organizations }) => organizations);
}

export function getOrganization(oid: string): Promise<Types.GetOrganizationResponse> {
    return http.get(`${Zeplin.urls.baseURL}/v2/organizations/${oid}`)
        .then<Types.GetOrganizationResponse>(handleResponse);
}

export function getOrganizationMembers(oid: string): Promise<Types.GetOrganizationMembersResponse> {
    const url = `${Zeplin.urls.baseURL}/v3/organizations/${oid}/members`;
    return http.get(url)
        .then<Types.GetOrganizationMembersResponse>(handleResponse);
}

export function getOrganizationMembersWithoutDetails(
    oid: string
): Promise<Types.GetOrganizationMembersWithoutDetailsResponse> {
    const url = `${Zeplin.urls.baseURL}/v3/organizations/${oid}/members?detailed=false`;
    return http.get(url)
        .then<Types.GetOrganizationMembersWithoutDetailsResponse>(handleResponse);
}

export function getMemberSuggestions(oid: string, role: string): Promise<string[]> {
    return http.get(`${Zeplin.urls.baseURL}/organizations/${oid}/members/${role}/suggestions`)
        .then<{ suggestedMembers: string[]; }>(handleResponse)
        .then(({ suggestedMembers }) => suggestedMembers);
}

export function inviteOrganizationMembers(
    oid: string, members: Types.OrganizationInvitee[]
): Promise<Types.InviteOrganizationMembersResponse> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members`;

    return http.post(url, { members }).then<Types.InviteOrganizationMembersResponse>(handleResponse);
}

export function reInviteOrganizationMembers(
    oid: string, members: string[]
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members/reinvite`;

    return http.post(url, { members }).then(checkResponseError);
}

export function updateOrganizationMemberRole(oid: string, mid: string, role: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members/${mid}/role`;

    return http.put(url, { role }).then(checkResponseError);
}

type memberIdWithApprovalState = { mid: string; approvalState?: Types.OrganizationMemberApprovalState; };
type UpdateOrganizationMembersRoleResponse = {
    updatedMembers: memberIdWithApprovalState[];
    reservedSeatCount: number;
};
export function updateOrganizationMembersRole(oid: string, memberIds: string[], role: string)
    : Promise<UpdateOrganizationMembersRoleResponse> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members/role`;

    return http.put(url, { memberIds, role }).then<UpdateOrganizationMembersRoleResponse>(handleResponse);
}

export function updateOrganizationSeats(
    oid: string, body: { seatCount: number; action: Types.OrganizationSeatsUpdateAction; }
): Promise<number> {
    return http.put(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/seats`, body)
        .then<{ reservedSeatCount: number; }>(handleResponse)
        .then(({ reservedSeatCount }) => reservedSeatCount);
}

export function updateInGracePeriodMemberApprovalState(oid: string, mid: string, approved: boolean)
    : Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/members/${mid}/approvalstate`, { approved })
        .then(checkResponseError);
}

export function updateInGracePeriodMembersApprovalState(oid: string, memberIds: string[], approved: boolean)
    : Promise<string[]> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/members/approvalstate`, { approved, memberIds })
        .then<string[]>(handleResponse);
}

export function getOrganizationScheduledSeatsUpdate(
    oid: string
): Promise<Types.OrganizationScheduledSeatsUpdateResponse> {
    return http.get(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/subscriptionSchedules/seats`)
        .then(response => handleResponse(response));
}

export function cancelOrganizationScheduledSeatsUpdate(oid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/subscriptionSchedules/seats`)
        .then(checkResponseError);
}

/**
 * Updates the access right of the member in given organization
 */
export function updateOrganizationMemberAccessibility(oid: string, mid: string, restricted: boolean): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members/${mid}/restricted`;

    return http.put(url, { restricted: Boolean(restricted) }).then(checkResponseError);
}

type UpdateOrganizationMembersAccessibilityResponse = { updatedMemberIds: string[]; };
export function updateOrganizationMembersAccessibility(oid: string, memberIds: string[], restricted: boolean)
    : Promise<UpdateOrganizationMembersAccessibilityResponse> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members/restricted`;

    return http.put(url, { memberIds, restricted: Boolean(restricted) })
        .then<UpdateOrganizationMembersAccessibilityResponse>(handleResponse);
}

export function updateOrganizationMembersTags(oid: string, members: Types.TagInfo[]): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members/tags`;

    return http.put(url, { members }).then(checkResponseError);
}

export function removeMembersFromOrganization(oid: string, memberIds: string[]):
    Promise<Types.RemoveMembersFromOrganizationResponse> {
    const url = `${Zeplin.urls.baseURL}/v2/organizations/${oid}/members`;
    return http.delete(url, { memberIds })
        .then<Types.RemoveMembersFromOrganizationResponse>(handleResponse);
}

export function transferOrganizationOwnership(
    oid: string, mid: string
): Promise<Types.TransferOrganizationOwnershipResponse> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/members/${mid}/ownership`)
        .then<Types.TransferOrganizationOwnershipResponse>(handleResponse);
}

export function getOrganizationBilling(oid: string): Promise<Types.GetBillingResponse> {
    return http.get(`${Zeplin.urls.baseURL}/organizations/${oid}/billing`)
        .then<Types.GetBillingResponse>(handleResponse);
}

/**
 * Get info about how proration and billing will happen
 * when a user/users added to organization or plan is changed.
 */
export function getOrganizationProration(
    oid: string, queryParams: { plan?: string; memberCount?: number; seatCount?: number; }
): Promise<Types.GetOrganizationProrationResponse> {
    const url = new URL(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/proration`);
    url.search = new URLSearchParams(queryParams as Record<string, string>).toString();

    return http.get(url.toString()).then<Types.GetOrganizationProrationResponse>(handleResponse);
}

export function updateOrganizationBilling(oid: string, billingInfo: Types.Billing): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/billing`, billingInfo)
        .then(checkResponseError);
}

export function updateOrganizationName(oid: string, body: { name: string; }): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/name`, body)
        .then(checkResponseError);
}

export function updateOrganizationMinRoleForMemberManagement(oid: string, body: { role: string; }): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/minroleformembermanagement`, body)
        .then(checkResponseError);
}

export function createOrganizationSubscription(
    oid: string, body: UpdateSubscriptionParameters): Promise<void | boolean> {
    return http.post(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/subscriptions`, body)
        .then(checkResponseErrorWithRedirect);
}

export function updateOrganizationSubscription(oid: string, body: UpdateSubscriptionParameters): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/subscriptions`, body)
        .then(checkResponseError);
}

export function getOrganizationCards(oid: string): Promise<Types.Card[]> {
    return http.get(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/cards`)
        .then<{ cards: Types.Card[]; }>(handleResponse)
        .then(({ cards }) => cards);
}

export function getOrganizationInvoices(
    oid: string, queryParams: Types.GetInvoicesParameters
): Promise<Types.GetInvoicesResponse> {
    const url = new URL(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/invoices`);
    url.search = new URLSearchParams(queryParams as Record<string, string>).toString();

    return http.get(url.toString()).then<Types.GetInvoicesResponse>(handleResponse);
}

export function calculateSubscriptionPrice(
    body: Types.CalculateSubscriptionPriceParameters
): Promise<Types.CalculateSubscriptionPriceResponse> {
    return http.post(`${Zeplin.urls.baseURL}/stripe/subscriptions/price`, body)
        .then<Types.CalculateSubscriptionPriceResponse>(handleResponse);
}

export function payOrganizationInvoice(oid: string, invoiceId?: string): Promise<Types.Invoice | Types.Invoice[]> {
    return http.post(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/invoices/${invoiceId ? `${invoiceId}/` : ""}pay`)
        .then<{ invoice: Types.Invoice; invoices: Types.Invoice[]; }>(handleResponse)
        .then(({ invoice, invoices }) => (invoiceId ? invoice : invoices));
}

export function payUserInvoice(invoiceId?: string): Promise<Types.Invoice | Types.Invoice[]> {
    return http.post(`${Zeplin.urls.baseURL}/stripe/users/invoices/${invoiceId ? `${invoiceId}/` : ""}pay`)
        .then<{ invoice: Types.Invoice; invoices: Types.Invoice[]; }>(handleResponse)
        .then(({ invoice, invoices }) => (invoiceId ? invoice : invoices));
}

export function getUserCredit(): Promise<Types.Credit> {
    return http.get(`${Zeplin.urls.baseURL}/stripe/users/credit`)
        .then<Types.Credit>(handleResponse);
}

export function updateOrganizationCard(oid: string, stripeToken: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/cards`, { stripeToken })
        .then(checkResponseError);
}

/**
 * Get profile of member of organization.
 * @param {String} oid organization id
 * @param {String} mid member id
 */
export function getOrganizationMemberProfile(oid: string, mid: string): Promise<Types.OrganizationMemberProfile> {
    return http.get(`${Zeplin.urls.baseURL}/organizations/${oid}/members/${mid}/profile`)
        .then<Types.OrganizationMemberProfile>(handleResponse);
}

/**
 * Requests an invitation to be a member of the specified organization.
 */
export function requestOrganizationInvitation(oid: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/requests/join`)
        .then(checkResponseError);
}

/**
 * Requests activity logs of the specified organization.
 */
export function requestOrganizationActivityLogs(oid: string, timePeriod: number): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/audit/logs`, {
        timePeriod
    }).then(checkResponseError);
}

/**
 * Returns the API config.
 */
export function getApiConfig(): Promise<Types.ApiConfig> {
    return http.get(`${Zeplin.urls.baseURL}/api/config`)
        .then<Types.ApiConfig>(handleResponse);
}

/**
 * Returns the plans.
 */
export function getPlans(): Promise<Types.SubscriptionPlan[]> {
    return http.get(`${Zeplin.urls.baseURL}/api/config/plans`)
        .then<Types.SubscriptionPlan[]>(handleResponse);
}

export function createOrganization(
    body: Types.CreateOrganizationParameters
): Promise<Types.CreateOrganizationResponse | null> {
    return http.post(`${Zeplin.urls.baseURL}/organizations`, body)
        .then<Types.CreateOrganizationResponse>(handleResponse)
        .then<Types.CreateOrganizationResponse | null>(handleResponseWithRedirect);
}

/**
 * Creates personal basic plan for use
 */
export function createPersonalPlan(body: CreatePersonalPlanParams) {
    return http.post(`${Zeplin.urls.baseURL}/stripe/users/subscriptions`, body).then(checkResponseErrorWithRedirect);
}

export function updatePersonalPlan(body: UpdatePersonalPlanParams) {
    return http.put(`${Zeplin.urls.baseURL}/stripe/users/subscriptions`, body).then(checkResponseErrorWithRedirect);
}

/**
 * Gets the SSO information of given organization
 */
export function getOrganizationSSO(oid: string): Promise<Types.OrganizationSSO> {
    return http.get(`${Zeplin.urls.baseURL}/organizations/${oid}/sso`)
        .then<Types.OrganizationSSO>(handleResponse);
}

/**
 * Gets the SAML metadata of given organization
 */
export function getOrganizationSSOSAMLMetadata(oid: string): Promise<{ metadata: string; }> {
    return http.get(`${Zeplin.urls.baseURL}/organizations/${oid}/sso/saml/metadata`)
        .then<{ metadata: string; }>(handleResponse);
}

/**
 * Enables Google SSO for given organization
 */
export function addOrganizationSSOGoogle(oid: string, code: string): Promise<Types.OrganizationSSO> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/sso/google`, {
        redirectUri: GOOGLE_AUTH_SETUP_SSO_REDIRECT_URI,
        code
    }).then<Types.OrganizationSSO>(handleResponse);
}

/**
 * Enables SAML SSO for given organization
 */
export function addOrganizationSSOSAML(
    oid: string,
    signOnURL: string,
    identifier: string,
    certificate: string
): Promise<Types.OrganizationSSO> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/sso/saml`, {
        signOnURL,
        identifier,
        certificate
    }).then<Types.OrganizationSSO>(handleResponse);
}

export function updateOrganizationSSOPolicy(oid: string, policy: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/sso/policy`, { policy })
        .then(checkResponseError);
}

export function updateOrganizationSSOSessionDuration(oid: string, sessionDuration: number): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/sso/sessionduration`, { sessionDuration })
        .then(checkResponseError);
}

export function updateOrganizationAllowExternalMembers(oid: string, allowExternalMembers: boolean): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/allowexternalmembers`, { allowExternalMembers })
        .then(checkResponseError);
}

export function updateOrganizationSSOProvider(
    oid: string,
    signOnURL: string,
    identifier: string,
    certificate: string
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/sso/provider`, {
        signOnURL,
        identifier,
        certificate
    }).then(checkResponseError);
}

export function deleteOrganizationSSOSAML(oid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/organizations/${oid}/sso/saml`)
        .then(checkResponseError);
}

export function deleteOrganizationSSOGoogle(oid: string, code: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/organizations/${oid}/sso/google`,
        { code, redirectUri: GOOGLE_AUTH_REMOVE_SSO_REDIRECT_URI })
        .then(checkResponseError);
}

export function getCoupon(couponId: string): Promise<Types.Coupon> {
    return http.get(`${Zeplin.urls.baseURL}/stripe/coupons/${couponId}`)
        .then<Types.Coupon>(handleResponse);
}

export function addOrganizationWorkflowStatus(
    oid: string,
    workflowStatus: Omit<Types.WorkflowStatus, "_id">
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/workflowStatuses`, workflowStatus)
        .then<Types.CreationResponse>(handleResponse);
}

export function updateOrganizationWorkflowStatus(
    oid: string,
    wsid: string,
    workflowStatus: Partial<Types.WorkflowStatus>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/workflowStatuses/${wsid}`, workflowStatus)
        .then(checkResponseError);
}

export function deleteOrganizationWorkflowStatus(oid: string, wsid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/organizations/${oid}/workflowStatuses/${wsid}`)
        .then(checkResponseError);
}

export function getOrganizationSCIMSettings(oid: string): Promise<Types.GetOrganizationSCIMSettingsResponse> {
    return http.get(`${Zeplin.urls.baseURL}/organizations/${oid}/scim`)
        .then<Types.GetOrganizationSCIMSettingsResponse>(handleResponse);
}

export function getOrganizationSCIMTokens(oid: string): Promise<Types.GetSCIMTokensResponse[]> {
    return http.get(`${Zeplin.urls.baseURL}/organizations/${oid}/scim/tokens`)
        .then<{tokens: Types.GetSCIMTokensResponse[];}>(handleResponse)
        .then(response => response.tokens);
}

export function createOrganizationSCIMToken(oid: string): Promise<Types.SCIMToken> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/scim/tokens`)
        .then<Types.SCIMToken>(handleResponse);
}

export function deleteOrganizationSCIMToken(oid: string, accessTokenId: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/organizations/${oid}/scim/tokens/${accessTokenId}`)
        .then(checkResponseError);
}

export function updateOrganizationSCIMStatus(oid: string, enabled: boolean): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/scim/status`, { enabled })
        .then(checkResponseError);
}

export function updateOrganizationSCIMRole(oid: string, role: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/scim/role`, { role })
        .then(checkResponseError);
}

export function updateOrganizationSCIMAccessibility(oid: string, restricted: boolean): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/scim/restricted`, { restricted })
        .then(checkResponseError);
}

// MARK: Project endpoints
export function getProjects(oid?: string | null): Promise<Types.Project[]> {
    const url = oid
        ? `${Zeplin.urls.baseURL}/v2/organizations/${oid}/projects`
        : `${Zeplin.urls.baseURL}/v3/projects`;

    return http.get(url, { cache: "no-cache" })
        .then<{ projects: Types.Project[]; }>(handleResponse)
        .then(({ projects }) => projects);
}

export function getOrganizationMemberFocusedBarrels(oid: string, mid: string): Promise<Types.FocusedBarrel[]> {
    const url = `${Zeplin.urls.baseURL}/v2/organizations/${oid}/members/${mid}/focus`;

    return http.get(url).then<{ focusedBarrels: Types.FocusedBarrel[]; }>(handleResponse)
        .then(({ focusedBarrels }) => focusedBarrels);
}

export function addBarrelToOrganizationMemberFocus(
    { oid, mid, bid, barrelType }: Types.OrganizationMemberFocusParameters
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members/${mid}/focus`;
    const body = {
        [barrelType === BarrelType.PROJECT ? "pid" : "stid"]: bid
    };

    return http.post(url, body).then(checkResponseError);
}

export function removeBarrelFromOrganizationMemberFocus(
    { oid, mid, bid, barrelType }: Types.OrganizationMemberFocusParameters
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/members/${mid}/focus`;
    const body = {
        [barrelType === "project" ? "pid" : "stid"]: bid
    };

    return http.delete(url, body).then(checkResponseError);
}

/**
 * Get section data for the given id, if the user has access to it
 * @param {String} oid
 */
export function getOrganizationSection(oid: string, oseid: string): Promise<Types.GetOrganizationSectionResponse> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/sections/${oseid}`;
    return http.get(url).then<Types.GetOrganizationSectionResponse>(handleResponse);
}

/**
 * Get visible sections for the given organization, filtered by the member's access
 * @param {String} oid
 */
export function getOrganizationSections(oid: string, oseid?: string): Promise<Types.OrganizationSections> {
    const url = `${Zeplin.urls.baseURL}/v2/organizations/${oid}/sections${oseid ? `?oseid=${oseid}` : ""}`;
    return http.get(url)
        .then<Types.OrganizationSections>(handleResponse);
}

export function createOrganizationSection({
    oid,
    index,
    name,
    description,
    projects
}: Types.CreateOrganizationSectionParameters): Promise<Types.CreationResponse> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/sections`;
    return http.post(url, { index, name, description, projects })
        .then<Types.CreationResponse>(handleResponse);
}

export function updateOrganizationSection(
    { oid, oseid, ...sectionData }: Types.UpdateOrganizationSectionParameters
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/sections/${oseid}`;
    return http.put(url, sectionData)
        .then(checkResponseError);
}

export function deleteOrganizationSection(oid: string, oseid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/organizations/${oid}/sections/${oseid}`)
        .then(checkResponseError);
}

/**
 * Add project ids to a section by removing them from other sections.
 *
 * @param {String} oid Organization id
 * @param {String} oseid Organization section id
 * @param {int} index Position that the projects should be inserted within the section
 * @param {String[]} projects Project ids that should be added to section
 */
export function updateOrganizationSectionProjects(
    oid: string, oseid: string, index: number, projects: string[]
): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/organizations/${oid}/sections/${oseid}/projects`, {
            index,
            projects
        })
        .then(checkResponseError);
}

export function updateOrganizationSectionsOrder(oid: string, index: number, sections: string[]): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/organizations/${oid}/sections/order`;
    return http.put(url, { index, sections }).then(checkResponseError);
}

export function createOrganizationSectionLink(
    oid: string, oseid: string, title: string, url: string
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/sections/${oseid}/links`, { title, url })
        .then<Types.CreationResponse>(handleResponse);
}

export function updateOrganizationSectionLink({
    oid, oseid, oselid, title, url
}: Types.UpdateOrganizationSectionLinkParameters): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/sections/${oseid}/links/${oselid}`, { title, url })
        .then(checkResponseError);
}

export function deleteOrganizationSectionLink(oid: string, oseid: string, oselid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/organizations/${oid}/sections/${oseid}/links/${oselid}`).then(checkResponseError);
}

export function getProject(pid: string): Promise<Types.ProjectDetails> {
    return http.get(`${Zeplin.urls.baseURL}/v8/projects/${pid}`)
        .then(response => {
            refreshToken(response);

            return response.json().then(resJson => {
                if (!response.ok) {
                    throw createError(response.status, resJson);
                }

                if (response.status === http.STATUS.PARTIAL_CONTENT) {
                    resJson.dataStage = BarrelDataStage.Partial;
                } else {
                    resJson.dataStage = BarrelDataStage.Complete;
                }

                return resJson;
            });
        });
}

export async function getProjectActivities(
    pid: string, nextCursorId?: string
): Promise<Types.ProjectActivitiesResponse> {
    const url = new URL(`${Zeplin.urls.baseURL}/projects/${pid}/activities`);

    if (nextCursorId !== undefined) {
        url.search = new URLSearchParams({ cursorId: nextCursorId }).toString();
    }

    const response = await http.get(url.toString());

    return handleResponse<Types.ProjectActivitiesResponse>(response);
}

export function getProjectDetail(pid: string, sid: string, limit: number) {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/detail?previousScreensLimit=${limit || 10}&nextScreensLimit=${limit || 10}`)
        .then(response => {
            refreshToken(response);

            return response.json().then(resJson => {
                if (!response.ok) {
                    throw createError(response.status, resJson);
                }

                resJson.project.dataStage = BarrelDataStage.Complete;
                return resJson;
            });
        });
}

export function getProjectOrganization(pid: string): Promise<Types.GetBarrelOrganizationResponse> {
    return http.get(`${Zeplin.urls.baseURL}/v2/projects/${pid}/organization`)
        .then<Types.GetBarrelOrganizationResponse>(handleResponse);
}

export function activateProject(pid: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/activate`)
        .then(checkResponseError);
}

export function archiveProject(pid: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/archive`)
        .then(checkResponseError);
}

/**
 * Invite a user to the project. If they are not a member, an invitation email will be sent out.
 */
export function inviteProjectUser(
    pid: string, handle?: string, role?: string, restricted?: boolean
): Promise<Types.BarrelUser> {
    const body = handle ? { handle, role, restricted } : null;
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/users`, body)
        .then<Types.BarrelUser>(handleResponse);
}

export function inviteProjectUsersByTag(
    oid: string, pid: string, tag: string
): Promise<Types.InviteBarrelUsersResponse> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/projects/${pid}/users`, { tag })
        .then<Types.InviteBarrelUsersResponse>(handleResponse);
}

export function reinviteProjectUser(
    pid: string, uid: string
): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/users/${uid}/reinvite`)
        .then(checkResponseError);
}

/**
 * Transfers ownership of project with specified id to an owner with newOwnerId
 */
export function transferProjectOwnership(
    pid: string, newOwnerId: string, toOrganization = false
): Promise<Types.BarrelUser[]> {
    const target = toOrganization ? "organizations" : "users";
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/${target}/${newOwnerId}/ownership`;
    return http.post(url).then<Types.BarrelUser[]>(handleResponse);
}

/**
 * Updates the role of the specified user in the project.
 */
export function updateProjectUserRole(pid: string, mid: string, role: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/users/${mid}/role`, { role })
        .then(checkResponseError);
}

/**
 * Remove a user from the project. It also means leaving a project for the current user.
 */
export function removeUserFromProject(pid: string, mid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/users/${mid}`;
    return http.delete(url).then(checkResponseError);
}

export function deleteProject(pid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}`;
    return http.delete(url).then(checkResponseError);
}

export function copyProject(pid: string, body: Types.CopyProjectParameters): Promise<Types.CopyProjectResponse> {
    return http
        .post(`${Zeplin.urls.baseURL}/v3/projects/${pid}/copy`, body)
        .then(response => {
            if (!response.ok) {
                return response.json().then(resJson => {
                    throw createError(response.status, resJson);
                });
            }

            if (response.status === 202) {
                return { async: true };
            }

            return response.json().then(({ project, order }) => ({ async: false, barrel: project, order }));
        });
}

/**
 * Creates a new project in organization with oid,
 * If oid is "user" creates a new personal project in "user" account
 */
export function createProject({
    oid,
    densityScale = 1,
    name = "Untitled",
    oseid
}: Types.CreateBarrelParameters): Promise<Types.CreateBarrelResponse> {
    const targetSpace = oid && oid !== "user" ? `/organizations/${oid}` : "";

    return http
        .post(`${Zeplin.urls.baseURL}${targetSpace}/projects`, {
            name,
            densityScale,
            oseid
        })
        .then<Types.CreateBarrelResponse>(handleResponse);
}

export function updateProjectName(pid: string, name: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/name`, { name })
        .then(checkResponseError);
}

/**
 * Update description of the project.
 * @param {String} pid
 * @param {String} description
 */
export function updateProjectDescription(pid: string, description: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/description`, { description })
        .then(checkResponseError);
}

export function updateProjectDensityScale(pid: string, densityScale: number): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/densityscale`, { densityScale })
        .then(checkResponseError);
}

export function updateProjectAccessibility(pid: string, publicity: boolean): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/public`, { public: publicity })
        .then(checkResponseError);
}

export function moveScreen(spid: string, tpid: string, items: Types.ScreenItem[]): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/v2/projects/${spid}/screens/projects/${tpid}`, {
            items
        })
        .then(checkResponseError);
}

export function createSampleProject(): Promise<Types.CreateSampleProjectResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/onboarding/sample`)
        .then<Types.CreateSampleProjectResponse>(handleResponse);
}

type SectionDescriptor = {
    name: string;
    description?: string;
    index?: number;
    screens?: string[];
    parent?: string;
};
/**
 * Creates a section in target project at given index
 */
export function createSection(pid: string, sectionDescriptor: SectionDescriptor): Promise<Types.CreationResponse> {
    const requestBody: SectionDescriptor = {
        name: sectionDescriptor.name,
        parent: sectionDescriptor.parent
    };

    if ("description" in sectionDescriptor) {
        requestBody.description = sectionDescriptor.description;
    }

    if ("index" in sectionDescriptor) {
        requestBody.index = sectionDescriptor.index;
    }

    if ("screens" in sectionDescriptor) {
        requestBody.screens = sectionDescriptor.screens;
    }

    return http
        .post(`${Zeplin.urls.baseURL}/v2/projects/${pid}/sections`, requestBody)
        .then<Types.CreationResponse>(handleResponse);
}

/**
 * Add screen ids to a section by removing them from other sections.
 */
export function updateSectionScreens(
    pid: string, seid: string, index: number, screens: string[]
): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/projects/${pid}/sections/${seid}/screens`, {
            index,
            screens
        })
        .then(checkResponseError);
}

export function updateSectionScreensV2({
    pid,
    seid,
    index,
    items
}: Types.UpdateSectionsScreensParameters): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/v3/projects/${pid}/sections/${seid}/screens`, {
            index,
            items
        })
        .then(checkResponseError);
}

type DeleteSectionPayload = { pid: string; seid: string; deleteContents: boolean; };
export function deleteSection({ pid, seid, deleteContents }: DeleteSectionPayload): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/v2/projects/${pid}/sections/${seid}`, { deleteContents })
        .then(checkResponseError);
}

export function updateSection(
    pid: string,
    seid: string,
    body: {
        name?: string;
        description?: string;
    }
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/sections/${seid}`, body)
        .then(checkResponseError);
}

type UpdateSectionOrderPayload = { sections: string[]; to: number; parent?: string; };
export function updateSectionOrder(pid: string, payload: UpdateSectionOrderPayload): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/v2/projects/${pid}/sections/order`;
    return http.put(url, payload).then(checkResponseError);
}

export function transferSection(pid: string, seid: string, targetPid: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/v2/projects/${pid}/sections/${seid}/transfer`, { targetPid })
        .then(checkResponseError);
}

/**
 * Get Jira Integration Info
 */
export function getProjectJiraInfo(pid: string): Promise<Types.GetProjectJiraInfoResponse> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/integrations/jira`;

    return http.get(url)
        .then<Types.GetProjectJiraInfoResponse>(handleResponse);
}

/**
 * Add Slack integration to given project
 */
export function authProjectSlack(pid: string, code: string, redirectURI: string): Promise<Response> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/integrations/slacks/auth`;

    return http.post(url, { code, redirectURI })
        .then(response => {
            refreshToken(response);

            return response;
        });
}

/**
 * Get slack integration custom emoji list of the project.
 */
export function getProjectSlackIntegrationEmojis(pid: string): Promise<Types.GetSlackIntegrationEmojisResponse> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/integrations/slacks/emojis`)
        .then<Types.GetSlackIntegrationEmojisResponse>(handleResponse);
}

export function deleteProjectIntegration(pid: string, intype: string, inid: string): Promise<void> {
    return http
        .delete(`${Zeplin.urls.baseURL}/projects/${pid}/integrations/${intype}/${inid}`)
        .then(checkResponseError);
}

export function getProjectExtensions(pid: string): Promise<Types.BarrelExtension[]> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/extensions`)
        .then<{ extensions: Types.BarrelExtension[]; }>(handleResponse)
        .then(({ extensions }) => extensions);
}

export function removeExtensionFromProject(pid: string, eid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/extensions/${eid}`)
        .then(checkResponseError);
}

export function addExtensionToProject(pid: string, eid: string): Promise<Types.AddBarrelExtensionResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/extensions`, { eid })
        .then<Types.AddBarrelExtensionResponse>(handleResponse);
}

export function removeExtensionFromStyleguide(stid: string, eid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/extensions/${eid}`)
        .then(checkResponseError);
}

export function addExtensionToStyleguide(stid: string, eid: string): Promise<Types.AddBarrelExtensionResponse> {
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${stid}/extensions`, { eid })
        .then<Types.AddBarrelExtensionResponse>(handleResponse);
}

export function updateScreen(
    pid: string,
    sid: string,
    data: {
        name?: string;
        description?: string;
    }
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}`, data)
        .then(checkResponseError);
}

export function deleteScreen(pid: string, sid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}`;
    return http.delete(url).then(checkResponseError);
}

export function createVariantGroup(pid: string, screenIds: string[]): Promise<Types.VariantGroup> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/variantGroups`;
    return http.post(url, { screenIds }).then<Types.VariantGroup>(handleResponse);
}

export function addScreensToVariantGroup(pid: string, vgid: string, screenIds: string[]): Promise<Types.VariantGroup> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/variantGroups/${vgid}`;
    return http.put(url, { screenIds }).then<Types.VariantGroup>(handleResponse);
}

export function deleteScreens(pid: string, items: Types.ScreenItem[]): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens`;
    return http.delete(url, {
        items
    }).then(checkResponseError);
}

export function detachVariantGroup(pid: string, vgid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/variantGroups/${vgid}`;
    return http.delete(url).then(checkResponseError);
}

export function updateVariantGroupName(pid: string, vgid: string, name: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/variantGroups/${vgid}/name`;
    return http.put(url, { name }).then(checkResponseError);
}

export function updateVariantGroupScreenVariantName(
    pid: string,
    vgid: string,
    svid: string,
    variantName: string
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/variantGroups/${vgid}/screens/${svid}/variantName`;
    return http.put(url, { variantName }).then(checkResponseError);
}

export function updateVariantGroupScreensOrder(
    pid: string,
    vgid: string,
    svid: string,
    index: number
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/variantGroups/${vgid}/screens/order`;
    return http.put(url, { index, screenIds: [svid] }).then(checkResponseError);
}

export function removeScreenFromVariantGroup(pid: string, vgid: string, svid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/variantGroups/${vgid}/screens/${svid}`;
    return http.delete(url).then(checkResponseError);
}

/**
 * Add a color to the color palette of the project.
 * @param {String} pid Project id
 * @param {Object} color Color object
 * @param {string} requestId Request id
 */
export function createProjectColor(
    pid: string,
    color: Types.Color,
    requestId?: string
): Promise<Types.CreationResponse> {
    if (requestId) {
        Zeplin.headers.set("Zeplin-Request-Id", requestId);
    }

    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/colors`, color)
        .then<Types.CreationResponse>(handleResponse);
}

export function updateProjectColorsOrder(
    pid: string,
    body: Types.ColorsOrder
): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/projects/${pid}/colors/order`, body)
        .then(checkResponseError);
}

/**
 * Update a color in the color palette of the project.
 * @param {String} pid Project id
 * @param {String} cid Color id
 * @param {Object} color Color update data
 */
export function updateProjectColor(pid: string, cid: string, color: Partial<Types.Color>): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/colors/${cid}`, color)
        .then(checkResponseError);
}

/**
 * Update colors name in the color palette of the project.
 * @param {String} pid Project id
 * @param {Object} colors Colors update data
 */
export function updateProjectColorsName(
    pid: string,
    colors: { colors: Partial<Types.Color>[]; }
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/colors/name`, colors)
        .then(checkResponseError);
}

/**
 * Delete a color from the color palette of the project.
 * @param {String} pid Project id
 * @param {String} cid Color id
 */
export function deleteProjectColor(pid: string, cid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/colors/${cid}`)
        .then(checkResponseError);
}

/**
 * Delete all colors of the project.
 * @param {String} pid Project id
 */
export function deleteProjectColors(pid: string, data: { colors: string[]; }): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/colors`, data)
        .then(checkResponseError);
}

export function createProjectTextStyle(
    pid: string, textStyle: Omit<Types.TextStyle, "_id">
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/textStyles`, textStyle)
        .then<Types.CreationResponse>(handleResponse);
}

type TextStyleParameters = Partial<Omit<Types.TextStyle, "_id">>;
export function updateProjectTextStyle(pid: string, tsid: string, textStyle: TextStyleParameters): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/textStyles/${tsid}`, textStyle)
        .then(checkResponseError);
}

export function deleteProjectTextStyle(pid: string, tsid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/textStyles/${tsid}`)
        .then(checkResponseError);
}

export function deleteProjectAllTextStyles(pid: string, body: { textStyles: string[]; }): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/textStyles`, body)
        .then(checkResponseError);
}

type SpacingSectionParameters =
    Pick<Types.SpacingSection, "name" | "description"> & Partial<Pick<Types.SpacingSection, "spacingTokens">>;
export function createProjectSpacingSection(
    pid: string, spacingSection: SpacingSectionParameters
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/spacingsections`, spacingSection)
        .then<Types.CreationResponse>(handleResponse);
}

type UpdateSpacingSectionParameters = Pick<Types.SpacingSection, "name" | "description" | "baseTokenId">;
export function updateProjectSpacingSection(
    pid: string,
    ssid: string,
    updatedSpacingSection: UpdateSpacingSectionParameters
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/spacingsections/${ssid}`, updatedSpacingSection)
        .then(checkResponseError);
}

export function deleteProjectSpacingSection(pid: string, ssid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/spacingsections/${ssid}`)
        .then(checkResponseError);
}

type SpacingTokenParameters = Pick<Types.SpacingToken, "value" | "token" | "color">;
export function createProjectSpacingToken(
    pid: string,
    ssid: string,
    spacingToken: SpacingTokenParameters
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/spacingsections/${ssid}/spacingtokens`, spacingToken)
        .then<Types.CreationResponse>(handleResponse);
}

export function updateProjectSpacingToken(
    pid: string,
    ssid: string,
    sptid: string,
    updatedSpacingToken: Partial<SpacingTokenParameters>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/spacingsections/${ssid}/spacingtokens/${sptid}`, updatedSpacingToken)
        .then(checkResponseError);
}

export function deleteProjectSpacingToken(pid: string, ssid: string, sptid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/spacingsections/${ssid}/spacingtokens/${sptid}`)
        .then(checkResponseError);
}

export function updateProjectRemPreferences(pid: string, updatedRemPreferences: Partial<Types.RemPreferences>):
    Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/rempreferences`, updatedRemPreferences)
        .then(checkResponseError);
}

export function getProjectComponentSections(pid: string): Promise<Types.GetProjectComponentSectionsResponse> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections`)
        .then<Types.GetProjectComponentSectionsResponse>(handleResponse);
}

export function fetchStorybookStories(storybookUrl: string): Promise<StorybookIndexResponse> {
    const url = new URL(`${Zeplin.urls.baseURL}/storybook/stories`);
    const queryParams = { url: storybookUrl };

    url.search = new URLSearchParams(queryParams).toString();

    return http.get(url.toString()).then<StorybookIndexResponse>(handleResponse);
}

export function connectProjectToStorybook(pid: string, url: string) {
    return http.put(`${Zeplin.urls.baseURL}/v2/projects/${pid}/connectedcomponents`, {
        items: [],
        metadata: {
            storybook: { url }
        }
    }).then(checkResponseError);
}

export function updateProjectStorybookUrl(pid: string, url: string) {
    return http.put(`${Zeplin.urls.baseURL}/v2/projects/${pid}/connectedcomponents/metadata/storybook`, { url }).then(checkResponseError);
}

export function deleteProjectConnectedComponents(pid: string) {
    return http.delete(`${Zeplin.urls.baseURL}/v2/projects/${pid}/connectedcomponents`).then(checkResponseError);
}

export function createProjectConnectedComponentItem(
    pid: string,
    body: Types.CreateConnectedComponentItemParameters
): Promise<ConnectedComponent> {
    return http.post(`${Zeplin.urls.baseURL}/v2/projects/${pid}/connectedcomponents/items`, body)
        .then<ConnectedComponent>(handleResponse);
}

export function deleteProjectConnectedComponentItem(pid: string, itemId: string) {
    return http.delete(`${Zeplin.urls.baseURL}/v2/projects/${pid}/connectedcomponents/items/${itemId}`).then(checkResponseError);
}

export function getProjectConnectedComponents(pid: string): Promise<Types.GetConnectedComponentsResponse> {
    return http.get(`${Zeplin.urls.baseURL}/v2/projects/${pid}/connectedcomponents`)
        .then<Types.GetConnectedComponentsResponse>(handleResponse)
        .catch(() => ({ items: [], metadata: {} }));
}

export function getProjectLocalConnectedComponents(pid: string): Promise<Types.GetLocalConnectedComponentsResponse> {
    // TODO: [SB] Use v2 endpoint
    return fetch(`http://127.0.0.1:9756/projects/${pid}/connectedcomponents`)
        .then(response => {
            if (response.ok) {
                return response.json().then(resJson => resJson.connectedComponents);
            }

            return null;
        })
        .then<Types.GetLocalConnectedComponentsResponse>(localConnectedComponents => ({ localConnectedComponents }))
        .catch<Types.GetLocalConnectedComponentsResponse>(() => ({ localConnectedComponents: null }));
}

export function addProjectComponentPage(
    pid: string, body: Types.CreateComponentPageParameters
): Promise<ComponentPage> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/pages`, body)
        .then<ComponentPage>(handleResponse);
}

export function updateProjectComponentPage(
    pid: string, pgid: string, body: Types.UpdateComponentParameters
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/pages/${pgid}`, body)
        .then(checkResponseError);
}

export function deleteProjectComponentPages(pid: string, pgids: string[], deleteContents: boolean): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/pages`, { pgids, deleteContents })
        .then(checkResponseError);
}

export function deleteProjectComponentSectionsFromPage(pid: string, pgid: string, csids: string[]): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/pages/${pgid}/componentsections`, { csids })
        .then(checkResponseError);
}

export function transferProjectPages(
    pid: string, body: Types.TransferPagesParameters
): Promise<Types.MergePagesResponse> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/pages/transfer`, body)
        .then<Types.MergePagesResponse>(handleResponse);
}

export function mergeProjectPages(pid: string, body: Types.MergePagesParameters): Promise<Types.MergePagesResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/pages/merge`, body)
        .then<Types.MergePagesResponse>(handleResponse);
}

export function updateProjectPageComponentSectionsOrder(
    pid: string, pgid: string, body: Types.UpdatePageComponentSectionsOrderParameters
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/pages/${pgid}/componentsections/order`, body)
        .then(checkResponseError);
}

export function updateProjectPagesStyleguide(pid: string, tstid: string, pgids: string[]): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/pages/styleguides/${tstid}`, { pgids })
        .then(checkResponseError);
}

type CreateComponentSectionResponse = Types.CreationResponse & UpdateComponentVariantSectionsResponse;
export function addProjectComponentSection(
    pid: string, csids: string[] | undefined, pgid: string | undefined, body: Types.CreateComponentSectionParameters
): Promise<CreateComponentSectionResponse> {
    if (pgid) {
        return http
            .post(`${Zeplin.urls.baseURL}/projects/${pid}/pages/${pgid}/componentsections`, body)
            .then<CreateComponentSectionResponse>(handleResponse);
    }

    return http
        .post(
            `${Zeplin.urls.baseURL}/projects/${pid}/componentsections${
                csids?.length ? `/${csids.join("/")}/componentsections` : ""
            }`,
            body
        )
        .then<CreateComponentSectionResponse>(handleResponse);
}

/**
 * Update component section's name or description.
 *
 * @param {String} pid Project id
 * @param {String[]} csids List of section ids identifying the component section
 * @param {Object} data Section data (name or description)
 */
export function updateProjectComponentSection(pid: string, csids: string[], data: Types.UpdateComponentParameters):
    Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csids.join("/")}`, data)
        .then(checkResponseError);
}

/**
 * Merge component sections:
 * - Moves source section's components to target section
 * - Deletes source section
 *
 * @param {String} pid Project id
 * @param {String[]} csids List of section ids identifying the source component section
 * @param {String[]} tcsids List of section ids identifying the target component section
 * @param {boolean} flatten Should flatten inner sections
 */
export function mergeProjectComponentSection(pid: string, csids: string[], tcsids: string[], flatten: boolean):
    Promise<void> {
    const componentSections = [{
        csid: csids.join("/"),
        tcsid: tcsids.join("/"),
        flatten
    }];

    return http
        .put(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/merge`, { componentSections })
        .then(checkResponseError);
}

export function convertProjectComponentSectionToVariantSection(
    pid: string,
    csid: string
): Promise<Types.UpdateComponentVariantDataResponse> {
    return http
        .post(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csid}/variant`)
        .then<Types.UpdateComponentVariantDataResponse>(handleResponse);
}

export function convertProjectVariantSectionToComponentSection(pid: string, csid: string): Promise<void> {
    return http
        .delete(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csid}/variant`)
        .then(checkResponseError);
}

export function createProjectComponentSectionVariantProperty(
    pid: string,
    csid: string,
    name: string,
    values?: string[]
): Promise<Types.CreationResponse> {
    return http
        .post(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csid}/variant/properties`, { name, values })
        .then<Types.CreationResponse>(handleResponse);
}

export function updateProjectComponentSectionVariantProperty(
    pid: string,
    csid: string,
    vpid: string,
    data: Types.UpdateComponentSectionVariantParameters
): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csid}/variant/properties/${vpid}`, data)
        .then(checkResponseError);
}

export function updateProjectComponentSectionVariantPropertiesOrder(
    pid: string,
    csid: string,
    data: Types.UpdateComponentSectionVariantPropertiesOrder
): Promise<void> {
    return http
        .post(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csid}/variant/properties/order`, data)
        .then(checkResponseError);
}

// eslint-disable-next-line max-params
export function updateProjectComponentsVariantProperty(
    pid: string,
    csid: string,
    vpid: string,
    components: string[],
    value: string
): Promise<Types.UpdateComponentVariantDataResponse> {
    return http
        .put(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csid}/components/variant/properties/${vpid}`, { components, value })
        .then<Types.UpdateComponentVariantDataResponse>(handleResponse);
}

export function deleteProjectComponentSectionVariantProperty(
    pid: string,
    csid: string,
    vpid: string
): Promise<void> {
    return http
        .delete(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csid}/variant/properties/${vpid}`)
        .then(checkResponseError);
}

export function createProjectComponentSectionVariantGroups(
    pid: string,
    csid: string,
    propertyId: string
): Promise<Partial<ComponentSection>> {
    return http
        .post(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csid}/variant/groups`, { propertyId })
        .then<Partial<ComponentSection>>(handleResponse);
}

export function updateProjectComponent(
    pid: string,
    csids: string[],
    coid: string,
    data: Types.UpdateComponentParameters
): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csids.join("/")}/components/${coid}`, data)
        .then(checkResponseError);
}

// Deleting a component inside a variant section may update variant data
type UpdateComponentVariantSectionResponse = { variantSection?: Types.UpdateComponentVariantDataResponse; };
export function deleteProjectComponent(
    pid: string,
    csids: string[],
    coid: string
): Promise<UpdateComponentVariantSectionResponse> {
    return http
        .delete(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csids.join("/")}/components/${coid}`)
        .then<UpdateComponentVariantSectionResponse>(handleResponse);
}

type UpdateComponentVariantSectionsResponse = { variantSections?: Types.UpdateComponentVariantDataResponse[]; };
export function deleteProjectComponents(pid: string, sectionComponents: Types.SectionComponents[]):
    Promise<UpdateComponentVariantSectionsResponse> {
    return http
        .delete(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/components`, { components: sectionComponents })
        .then<UpdateComponentVariantSectionsResponse>(handleResponse);
}

export function deleteProjectComponentSection(
    pid: string,
    csids: string[]
): Promise<void> {
    return http
        .delete(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csids.join("/")}`)
        .then<void>(checkResponseError);
}

export function updateProjectComponentsOrder(pid: string, body: Types.UpdateComponentsOrderParameters):
    Promise<UpdateComponentVariantSectionsResponse> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/components/order`, body)
        .then<UpdateComponentVariantSectionsResponse>(handleResponse);
}

export function updateProjectComponentSectionsOrder(pid: string, body: Types.UpdateComponentSectionsOrderParameters):
    Promise<UpdateComponentVariantSectionsResponse> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/order`, body)
        .then<UpdateComponentVariantSectionsResponse>(handleResponse);
}

export function updateProjectStyleguideLink(pid: string, styleguide: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/styleguidelink`, { styleguide })
        .then(checkResponseError);
}

export function removeProjectStyleguideLink(pid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/styleguidelink`)
        .then(checkResponseError);
}

export function getProjectIntegrationEvents(pid: string, inid: string): Promise<Types.SlackPreferencesEventsResponse> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/integrations/slacks/${inid}/events`)
        .then<Types.SlackPreferencesEventsResponse>(handleResponse);
}

export function addProjectIntegrationEvent(pid: string, inid: string, event: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/integrations/slacks/${inid}/events/${event}`)
        .then(checkResponseError);
}

export function deleteProjectIntegrationEvent(pid: string, inid: string, event: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/integrations/slacks/${inid}/events/${event}`)
        .then(checkResponseError);
}

export function getProjectTeamsIntegration(pid: string): Promise<Types.GetTeamsIntegrationResponse> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/integrations/microsoftTeams`)
        .then<Types.GetTeamsIntegrationResponse>(handleResponse);
}

/**
 * Fetches snapshot from cdn.
 */
export function fetchSnapshot(url: string): Promise<RequiredOnly<Partial<Types.Snapshot>, "layers" | "assets">> {
    return fetch(url)
        .then<Types.Snapshot>(handleResponse)
        .then(snapshot => {
            snapshot.layers ??= [];
            snapshot.assets ??= [];

            return snapshot as RequiredOnly<Types.Snapshot, "layers" | "assets">;
        })
        .catch(() => ({ layers: [], assets: [] }));
}

/**
 * Gets the snapshot of a screen with given version.
 */
export function getSnapshot(pid: string, sid: string, vid: string): Promise<Types.Snapshot> {
    const url = `${Zeplin.urls.baseURL}/v3/projects/${pid}/screens/${sid}/versions/${vid}/snapshot`;
    return http.get(url).then<Types.Snapshot>(response => {
        refreshToken(response);

        return response.json().then(snapshotResponse => {
            if (response.ok) {
                if (typeof snapshotResponse.snapshot === "string") {
                    return fetchSnapshot(snapshotResponse.snapshot);
                }

                return snapshotResponse.snapshot;
            }

            throw createError(response.status, snapshotResponse);
        });
    });
}

export function deleteProjectComponentVersion(pid: string, coid: string, vid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/components/${coid}/versions/${vid}`;
    return http.delete(url).then(checkResponseError);
}

export function deleteStyleguideComponentVersion(stid: string, coid: string, vid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${stid}/components/${coid}/versions/${vid}`;
    return http.delete(url).then(checkResponseError);
}

export function deleteVersion(pid: string, sid: string, vid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/versions/${vid}`;
    return http.delete(url).then(checkResponseError);
}

export function getScreenVersions(pid: string, sid: string): Promise<Types.GetScreenVersionsResponse> {
    const url = `${Zeplin.urls.baseURL}/v2/projects/${pid}/screens/${sid}/versions`;
    return http.get(url).then<Types.GetScreenVersionsResponse>(handleResponse);
}

export function getProjectComponentVersions(pid: string, coid: string) {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/components/${coid}/versions`;
    return http.get(url).then<Types.GetComponentVersionsResponse>(handleResponse);
}

export function getStyleguideComponentVersions(stid: string, coid: string) {
    const url = `${Zeplin.urls.baseURL}/styleguides/${stid}/components/${coid}/versions`;
    return http.get(url).then<Types.GetComponentVersionsResponse>(handleResponse);
}

export function getVersionAssets(pid: string, sid: string, vid: string): Promise<Types.GetAssetsResponse | null> {
    return http.get(`${Zeplin.urls.baseURL}/v2/projects/${pid}/screens/${sid}/versions/${vid}/assets`)
        .then<Types.GetAssetsResponse | null>(handleResponseSilently);
}

export function fetchAssets(url: string): Promise<Asset[]> {
    return fetch(url)
        .then<Asset[]>(handleResponse)
        .catch(() => []);
}

// eslint-disable-next-line max-params
export function processVersionAssetContent(
    pid: string,
    sid: string,
    vid: string,
    vaid: string,
    ovacid: string
): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/versions/${vid}/assets/${vaid}/contents/${ovacid}/process`)
        .then(checkResponseError);
}

export function getDotStates(body: Types.GetDotStatesParameters): Promise<Types.GetDotStatesResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/dots`, body).then<Types.GetDotStatesResponse>(handleResponse);
}

/**
 * Gets dots of a screen.
 * @param {*} pid project id
 * @param {*} sid screen id
 */
export function getDots(pid: string, sid: string): Promise<Types.GetDotsResponse> {
    const url = `${Zeplin.urls.baseURL}/v2/projects/${pid}/screens/${sid}/dots`;
    return http.get(url).then<Types.GetDotsResponse>(handleResponse);
}

/**
 * Gets dots of a project.
 * @param {*} pid project id
 */
export function getProjectDots(pid: string): Promise<Types.GetDotsResponse> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/dots`;
    return http.get(url).then<Types.GetDotsResponse>(handleResponse);
}

/**
 * Updates read status of dots.
 * @param {*} pid project id
 * @param {*} markedAsRead read status
 * @param {*} dots dot and screen id data
 */
export function updateDotReadStatus(
    pid: string,
    markedAsRead: boolean,
    dots: {
        did: string;
        sid: string;
    }[]): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/dots/status/read`;
    return http.put(url, {
        markedAsRead,
        dots
    }).then(checkResponseError);
}

/**
 * Gets annotations of a screen.
 * @param {*} pid project id
 * @param {*} sid screen id
 */
export function getAnnotations(pid: string, sid: string): Promise<Types.GetAnnotations> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/annotations`;
    return http.get(url).then<Types.GetAnnotations>(handleResponse);
}

/**
 * Create a dot on a screen.
 * @param {*} pid project id
 * @param {*} sid screen id
 */
export function createDot(pid: string, sid: string, dot: Types.CreateDotParameters): Promise<Types.Dot> {
    return http.post(`${Zeplin.urls.baseURL}/v2/projects/${pid}/screens/${sid}/dots`, dot)
        .then<Types.Dot>(handleResponse);
}

export function createAnnotation(
    pid: string,
    sid: string,
    annotation: Types.CreateAnnotationParameters
): Promise<Types.Annotation> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/annotations`, annotation)
        .then<Types.Annotation>(handleResponse);
}

/**
 * Update a dot on a screen.
 * @param {*} pid project id
 * @param {*} sid screen id
 * @param {*} did dot id
 * @param data Dot data (x, y, color, or status)
 */
export function updateDot(pid: string, sid: string, did: string, data: Types.UpdateDotParameters): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/dots/${did}`, data)
        .then(checkResponseError);
}

// eslint-disable-next-line max-params
export function updateAnnotation(
    pid: string,
    sid: string,
    atid: string,
    field: "coordinates" | "type" | "text",
    data: Types.UpdateAnnotationParameters): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/annotations/${atid}/${field}`, data)
        .then(checkResponseError);
}

// MARK: Component Annotations
export function createComponentAnnotation(
    pid: string,
    sid: string,
    annotation: Types.CreateAnnotationParameters
): Promise<Types.Annotation> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/componentAnnotations`, { ...annotation, screenId: sid })
        .then<Types.Annotation>(handleResponse);
}

export function getComponentAnnotations(pid: string): Promise<Types.Annotation[]> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/componentAnnotations`)
        .then<{ componentAnnotations: Types.Annotation[]; }>(handleResponse)
        .then(response => response.componentAnnotations);
}

// eslint-disable-next-line max-params
export function updateComponentAnnotation(
    pid: string,
    catid: string,
    field: "coordinates" | "type" | "text",
    data: Types.UpdateAnnotationParameters
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/componentAnnotations/${catid}/${field}`, data)
        .then(checkResponseError);
}

export function deleteAnnotationType(pid: string, sid: string, atid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/annotations/${atid}/type`;
    return http.delete(url).then(checkResponseError);
}

export function deleteComponentAnnotationType(pid: string, catid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/componentAnnotations/${catid}/type`;
    return http.delete(url).then(checkResponseError);
}

export function deleteAnnotation(pid: string, sid: string, atid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/annotations/${atid}`;
    return http.delete(url).then(checkResponseError);
}

export function deleteComponentAnnotation(pid: string, catid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/componentAnnotations/${catid}`;
    return http.delete(url).then(checkResponseError);
}

export function unlinkComponentAnnotation(
    pid: string,
    sid: string,
    catid: string,
    coordinate: { x: number; y: number; }
) {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/componentAnnotations/${catid}/unlink`;
    return http.post(url, coordinate).then<Types.Annotation>(handleResponse);
}

/**
 * Delete a dot on a screen.
 * @param {*} pid project id
 * @param {*} sid screen id
 * @param {*} did dot id
 */
export function deleteDot(pid: string, sid: string, did: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/dots/${did}`;
    return http.delete(url).then(checkResponseError);
}

export function updateCommitMessage(pid: string, sid: string, vid: string, message: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/versions/${vid}/commit/message`;
    return http.put(url, { message }).then(checkResponseError);
}

export function updateProjectComponentCommitMessage(
    pid: string, coid: string, vid: string, message: string
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/components/${coid}/versions/${vid}/commit/message`;
    return http.put(url, { message }).then(checkResponseError);
}

export function updateStyleguideComponentCommitMessage(
    stid: string, coid: string, vid: string, message: string
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${stid}/components/${coid}/versions/${vid}/commit/message`;
    return http.put(url, { message }).then(checkResponseError);
}

/**
 * Creates a comment on a dot
 * @param {*} pid project id
 * @param {*} sid screen id
 * @param {*} did dot id
 * @param {*} note note content of the comment
 */
export function createDotComment(pid: string, sid: string, did: string, note: string): Promise<Types.DotComment> {
    const url = `${Zeplin.urls.baseURL}/v2/projects/${pid}/screens/${sid}/dots/${did}/comments`;
    return http.post(url, { note }).then<Types.DotComment>(handleResponse);
}

/**
 * Edits a comment on a dot
 * @param {*} pid project id
 * @param {*} sid screen id
 * @param {*} did dot id
 * @param {*} cid comment id
 * @param {*} body body containing edited note
 */
// eslint-disable-next-line max-params
export function updateDotCommentNote(pid: string, sid: string, did: string, cmid: string, note: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/dots/${did}/comments/${cmid}/note`;
    return http.put(url, { note }).then(checkResponseError);
}

type AddReactionResponse = Pick<Types.Reaction, "_id">;
// eslint-disable-next-line max-params
export function addDotCommentReaction(
    pid: string, sid: string, did: string, cmid: string, reaction: string
): Promise<AddReactionResponse> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/dots/${did}/comments/${cmid}/reactions`;
    return http.post(url, { type: reaction }).then<AddReactionResponse>(handleResponse);
}

// eslint-disable-next-line max-params
export function removeDotCommentReaction(
    pid: string, sid: string, did: string, cmid: string, cmrid: string
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/dots/${did}/comments/${cmid}/reactions/${cmrid}`;
    return http.delete(url).then(checkResponseError);
}

/**
 * Deletes a comment on a dot
 * @param {*} pid project id
 * @param {*} sid screen id
 * @param {*} did dot id
 * @param {*} cmid comment id
 */
export function deleteComment(pid: string, sid: string, did: string, cmid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/dots/${did}/comments/${cmid}`;
    return http.delete(url).then(checkResponseError);
}

export function updateProjectThumbnail(pid: string, thumbnail: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/thumbnail`;
    return http.put(url, { thumbnail }).then(checkResponseError);
}

export function updateProjectPreviewBackgroundColor(
    pid: string,
    previewBackgroundColor: RawColor | null
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/previewbackgroundcolor`;
    return http.put(url, { previewBackgroundColor }).then(checkResponseError);
}

export function createProjectTag(pid: string, tag: Partial<Types.Tag>): Promise<Types.Tag> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/tags`;
    return http.post(url, tag).then<Types.Tag>(handleResponse);
}

type UpdateProjectTagBody = Partial<Pick<Types.Tag, "name" | "order" | "color">> | { "tagGroup": string; };
export function updateProjectTag(pid: string, tid: string, data: UpdateProjectTagBody): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/v2/projects/${pid}/tags/${tid}`;
    return http.put(url, data).then(checkResponseError);
}

export function deleteProjectTag(pid: string, tid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/tags/${tid}`;
    return http.delete(url).then(checkResponseError);
}

export function addProjectTagToScreens(pid: string, tid: string, screens: string[]): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/tags/${tid}/screens`;
    return http.put(url, { screens }).then(checkResponseError);
}

export function removeProjectTagFromScreens(pid: string, tid: string, screens: string[]): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/tags/${tid}/screens`;
    return http.delete(url, { screens }).then(checkResponseError);
}

export function createProjectTagGroup(pid: string, name: string, type?: "member"| "status", showOnThumbnails?: boolean): Promise<Types.TagGroup> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/tagGroups`;
    return http.post(url, { name, type, showOnThumbnails }).then<Types.TagGroup>(handleResponse);
}

export function updateProjectTagGroup(pid: string, tgid: string, data: Pick<Types.TagGroup, "name" & "order">): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/tagGroups/${tgid}`;
    return http.put(url, data).then(checkResponseError);
}

export function updateProjectTagGroupOrder(
    pid: string,
    tgid: string,
    data: { orderBy: TagGroupOrderBy; }
): Promise<Types.UpdateProjectTagGroupOrderResponse> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/tagGroups/${tgid}/tags/order`;
    return http.put(url, data).then<Types.UpdateProjectTagGroupOrderResponse>(handleResponse);
}

export function deleteProjectTagGroup(pid: string, tgid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/tagGroups/${tgid}`;
    return http.delete(url).then(checkResponseError);
}

/**
 * Sets multiple projects workflow statuses
 */
export function updateProjectsWorkflowStatus(projects: string[], wsid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/workflowStatus`, { projects, wsid })
        .then(checkResponseError);
}

export function deleteProjectsWorkflowStatus(projects: string[]): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/workflowStatus`, { projects })
        .then(checkResponseError);
}

export function getProjectDesignTokens(
    pid: string,
    includeLinkedStyleguides: boolean
): Promise<Types.GetBarrelDesignTokensResponse> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/designTokens?includeLinkedStyleguides=${includeLinkedStyleguides}`)
        .then<Types.GetBarrelDesignTokensResponse>(handleResponse);
}

export function getProjectDesignTokensFile(
    pid: string,
    includeLinkedStyleguides: boolean
): Promise<{ blob: Blob; name: string; }> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/designTokens/download?includeLinkedStyleguides=${includeLinkedStyleguides}`)
        .then(handleResponseBlob);
}

export function getProjectComponentVersionAssets(
    pid: string, csids: string[], coid: string, vid: string
): Promise<Types.GetAssetsResponse | null> {
    return http.get(`${Zeplin.urls.baseURL}/v2/projects/${pid}/componentsections/${csids.join("/")}/components/${coid}/versions/${vid}/assets`)
        .then<Types.GetAssetsResponse | null>(handleResponseSilently);
}

// eslint-disable-next-line max-params
export function processProjectComponentVersionAssetContent(
    pid: string,
    csids: string[],
    coid: string,
    vid: string,
    vaid: string,
    ovacid: string
): Promise<void> {
    return http.post(
        `${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${csids.join("/")}/components/${coid}/versions/${vid}/assets/${vaid}/contents/${ovacid}/process`
    ).then(checkResponseError);
}

export function getJoinedOrganizationProjects(): Promise<Types.JoinedOrganizationProjects> {
    return http.get(`${Zeplin.urls.baseURL}/users/emailnotifications/projects`)
        .then<Types.JoinedOrganizationProjects>(handleResponse);
}

// MARK: User endpoints

/**
 * Gets notifications before specified date and count
 *
 * @param dateBefore Date
 * @param {Number} count amount of notifications to be fetched
 */
export function getNotifications(dateBefore: string | null, count: number): Promise<Types.GetNotificationsResponse> {
    let url = `${Zeplin.urls.baseURL}/notifications?count=${count}`;
    if (dateBefore) {
        url += `&date_before=${dateBefore}`;
    }

    return http.get(url).then<Types.GetNotificationsResponse>(handleResponse);
}

export function updateNotificationEventsReadStatus(eventIds: string[], read: boolean): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/notifications/events`, {
        eventIds,
        markedAsRead: read
    }).then(checkResponseError);
}

export function getUserAcquaintances(): Promise<Types.GetUserAcquaintancesResponse> {
    return http.get(`${Zeplin.urls.baseURL}/users/acquaintances`)
        .then<Types.GetUserAcquaintancesResponse>(handleResponse);
}

type UpdateNotificationLastReadTimeResponse = { _id: string; notificationLastReadTime: string; };
export function updateNotificationLastReadTime(): Promise<UpdateNotificationLastReadTimeResponse> {
    return http.put(`${Zeplin.urls.baseURL}/users/notificationLastReadTime`)
        .then<UpdateNotificationLastReadTimeResponse>(handleResponse);
}

export function logoutUser(): Promise<Response> {
    return http.post(`${Zeplin.urls.baseURL}/users/logout`);
}

export function getSsoLoginParams(email: string): Promise<Types.SSOLoginParams> {
    const url = `${Zeplin.urls.baseURL}/users/auth/${email}/sso/loginparams`;
    return http.get(url).then<Types.SSOLoginParams>(handleResponse);
}

export function requestPassword(handle: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/users/requests/password`;
    return http.post(url, { handle }).then(checkResponseError);
}

export function resetPassword(password: string): Promise<Types.UserProfileWithToken> {
    const url = `${Zeplin.urls.baseURL}/users/resets/password`;
    return http.post(url, { password }).then<Types.UserProfileWithToken>(handleResponse);
}

/**
 * Unlink Zeplin user's account from SSO provider.
 */
export function deleteUserSSOProvider(
    type: "figma" | "google", code: string, redirectUri: string, password?: string
): Promise<UserTokenResponse> {
    return http.delete(`${Zeplin.urls.baseURL}/users/sso/${type}`, {
        password,
        code,
        redirectUri
    }).then<UserTokenResponse>(handleResponse);
}

/**
 * Turn on/off the email notifications.
 */
export function updateEmailNotifications(state: boolean): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/emailNotifications`, { emailNotifications: state })
        .then(checkResponseError);
}

export function updateEmailNotificationPreferences(
    preferences: Partial<Types.EmailNotificationPreferences>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/emailNotifications/preferences`, preferences)
        .then(checkResponseError);
}

export function muteProjectNotifications(pid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/notificationsettings/projects/${pid}/mute`)
        .then(checkResponseError);
}

export function muteStyleguideNotifications(stid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/notificationsettings/styleguides/${stid}/mute`)
        .then(checkResponseError);
}

export function unmuteProjectNotifications(pid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/notificationsettings/projects/${pid}/unmute`)
        .then(checkResponseError);
}

export function unmuteStyleguideNotifications(stid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/notificationsettings/styleguides/${stid}/unmute`)
        .then(checkResponseError);
}

export function updateFollowedProjectNotifications(
    followedProjectIds: string[],
    unfollowedProjectIds: string[]
): Promise<string[]> {
    return http.put(`${Zeplin.urls.baseURL}/users/emailnotifications/projects`, {
        followedProjectIds,
        unfollowedProjectIds
    }).then<string[]>(handleResponse);
}

export function updateUserEmotar(emotar: string | null): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/emotar`, { emotar }).then(checkResponseError);
}

export function cancelUserSubscription(body: Types.CancelSubscriptionParameters): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/stripe/users/subscriptions`;
    return http.delete(url, body).then(checkResponseError);
}

type UpdateSubscriptionParameters = Pick<Types.SubscriptionParameters, "plan" | "coupon" | "stripeToken" | "reservedSeatCount" | "billing">;
export function updateUserSubscription(body: UpdateSubscriptionParameters): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/stripe/users/subscriptions`;
    return http.put(url, body).then(checkResponseError);
}

export function createUserSubscription(body: Types.SubscriptionParameters): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/stripe/users/subscriptions`;
    return http.post(url, body).then(checkResponseError);
}

export function cancelOrganizationSubscription(oid: string, body: Types.CancelSubscriptionParameters): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/stripe/organizations/${oid}/subscriptions`;
    return http.delete(url, body).then(checkResponseError);
}

type DowngradeOrganizationSubscriptionParameters = Pick<Types.SubscriptionParameters, "plan" | "coupon">;
export function downgradeOrganizationSubscription(
    oid: string, body: DowngradeOrganizationSubscriptionParameters
): Promise<void | boolean> {
    const url = `${Zeplin.urls.baseURL}/stripe/organizations/${oid}/subscriptions/downgrade`;
    return http.post(url, body).then(checkResponseErrorWithRedirect);
}

export function transferUserSubscription(email: string): Promise<{ paymentPlan: string; }> {
    const url = `${Zeplin.urls.baseURL}/users/subscriptions/${email}/ownership`;
    return http.post(url).then<{ paymentPlan: string; }>(handleResponse);
}

/**
 * Creates a new conversation in intercom
 * @param {string} message to be sent to intercom
 */
export function createIntercomMessage(message: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/intercom/messages`, { message })
        .then(checkResponseError);
}

export function getUserUpcomingInvoice(): Promise<Types.GetUserUpcomingInvoiceResponse> {
    return http.get(`${Zeplin.urls.baseURL}/stripe/users/invoices/next`)
        .then<Types.GetUserUpcomingInvoiceResponse>(handleResponse);
}

export function getOrganizationUpcomingInvoice(oid: string): Promise<Types.GetOrganizationUpcomingInvoiceResponse> {
    return http.get(`${Zeplin.urls.baseURL}/stripe/organizations/${oid}/invoices/next`)
        .then<Types.GetOrganizationUpcomingInvoiceResponse>(handleResponse);
}

/**
 * Creates a tag for current user in the intercom
 */
export function createIntercomUserTag(name: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/intercom/users/tags`, { name })
        .then(checkResponseError);
}

// MARK: Account endpoints

export async function getAccounts(): Promise<Types.Account[]> {
    const response = await http.get(`${Zeplin.urls.baseURL}/accounts`);
    return handleResponse<Types.Account[]>(response);
}

export async function getAccountDetail(aid: string): Promise<Types.AccountDetail> {
    const response = await http.get(`${Zeplin.urls.baseURL}/accounts/${aid}`);
    return handleResponse<Types.AccountDetail>(response);
}

export async function updateDomainCaptureSettings(
    aid: string,
    doid: string,
    data: Types.CaptureSettings): Promise<void> {
    const response = await http.put(`${Zeplin.urls.baseURL}/accounts/${aid}/domains/${doid}/captureSettings`, data);
    return checkResponseError(response);
}

export async function createDomain(
    aid: string,
    data: Types.CreateDomainParameters
): Promise<Types.AccountDomain> {
    const response = await http.post(`${Zeplin.urls.baseURL}/accounts/${aid}/domains`, data);
    return handleResponse<Types.AccountDomain>(response);
}

export async function deleteDomain(
    aid: string,
    doid: string
): Promise<void> {
    const response = await http.delete(`${Zeplin.urls.baseURL}/accounts/${aid}/domains/${doid}`);
    return checkResponseError(response);
}

export async function addAccountAdmin(aid: string, email: string): Promise<Types.AddAccountMemberResponse> {
    const members = [{ handle: email }];
    const response = await http.post(`${Zeplin.urls.baseURL}/accounts/${aid}/members`, { members });
    return handleResponse<Types.AddAccountMemberResponse>(response);
}

export async function removeAccountAdmin(aid: string, adminId: string): Promise<void> {
    const members = [adminId];
    const response = await http.delete(`${Zeplin.urls.baseURL}/accounts/${aid}/members`, { members });
    return checkResponseError(response);
}

// MARK: Styleguide endpoints
export function getStyleguides(oid: string | null): Promise<Types.Styleguide[]> {
    const url = oid
        ? `${Zeplin.urls.baseURL}/organizations/${oid}/styleguides`
        : `${Zeplin.urls.baseURL}/styleguides`;

    return http.get(url, { cache: "no-cache" })
        .then<{ styleguides: Types.Styleguide[]; }>(handleResponse)
        .then(({ styleguides }) => styleguides);
}

export function getStyleguide(
    sid: string,
    { projectId, styleguideId }: { projectId?: string; styleguideId?: string; } = {}
): Promise<Types.StyleguideDetails> {
    const headers = new Headers(Zeplin.headers);

    if (projectId) {
        headers.append("Zeplin-Project-Id", projectId);
    } else if (styleguideId) {
        headers.append("Zeplin-Styleguide-Id", styleguideId);
    }

    return fetch(`${Zeplin.urls.baseURL}/v2/styleguides/${sid}`, {
        headers
    }).then(response => {
        refreshToken(response);

        return response.json().then(resJson => {
            if (!response.ok) {
                throw createError(response.status, resJson);
            }

            if (response.status === http.STATUS.PARTIAL_CONTENT) {
                resJson.dataStage = BarrelDataStage.Partial;
            } else {
                resJson.status = "active";
                resJson.dataStage = BarrelDataStage.Complete;
            }

            return resJson;
        });
    });
}

export function getStyleguideOrganization(sid: string): Promise<Types.GetBarrelOrganizationResponse> {
    return http.get(`${Zeplin.urls.baseURL}/v2/styleguides/${sid}/organization`)
        .then<Types.GetBarrelOrganizationResponse>(handleResponse);
}

/**
 * Get the projects linked to the styleguide.
 */
type StyleguideProject = Pick<Types.Project, "_id" | "name" | "updated" | "type">;
type LinkedProjects = { projects: StyleguideProject[]; };
export function getStyleguideProjects(sid: string): Promise<LinkedProjects> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${sid}/projects`)
        .then<LinkedProjects>(handleResponse);
}

/**
 * Creates a new styleguide in organization with oid,
 * If oid is "user" creates a new personal styleguide in "user" account
 */
export function createStyleguide({
    oid,
    name = "Untitled"
}: Types.CreateBarrelParameters): Promise<Types.CreateBarrelResponse> {
    const targetSpace = oid && oid !== "user" ? `/organizations/${oid}` : "";

    return http
        .post(`${Zeplin.urls.baseURL}${targetSpace}/styleguides`, {
            name,
            densityScale: 1
        })
        .then<Types.CreateBarrelResponse>(handleResponse);
}

export function deleteStyleguide(sid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${sid}`;
    return http.delete(url).then(checkResponseError);
}

export function copyStyleguide(sid: string, body: Types.CopyStyleguideParameters):
    Promise<Types.CopyStyleguideResponse> {
    return http
        .post(`${Zeplin.urls.baseURL}/styleguides/${sid}/copy`, body)
        .then(response => {
            if (!response.ok) {
                return response.json().then(resJson => {
                    throw createError(response.status, resJson);
                });
            }

            if (response.status === 202) {
                return { async: true };
            }

            return response.json().then(({ styleguide, order }) => ({ async: false, barrel: styleguide, order }));
        });
}

export function archiveStyleguide(sid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${sid}/archive`;
    return http.post(url).then(checkResponseError);
}

export function activateStyleguide(sid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${sid}/activate`;
    return http.post(url).then(checkResponseError);
}

export function checkFigmaToken() {
    return http.head(`${Zeplin.urls.baseURL}/figma/oauth/token`)
        .then(response => {
            if (!response.ok) {
                throw createError(response.status);
            }
        });
}

export function getFigmaSyncData(stid: string): Promise<FigmaIntegration> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/figma`)
        .then<FigmaIntegration>(handleResponse);
}

export function removeFigmaSyncData(stid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/figma`)
        .then(checkResponseError);
}

export function updateFigmaFile(stid: string, fileKey: string): Promise<Types.FigmaSyncUpdateFile> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/figma/fileKey`, { fileKey })
        .then<Types.FigmaSyncUpdateFile>(handleResponse);
}

export function syncStyleguideLibrary(stid: string): Promise<Types.FigmaSyncProgressInfo> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/figma/sync`;

    return http.post(url).then<Types.FigmaSyncProgressInfo>(handleResponse);
}

export function syncStyleguideLibraryWithFileId(
    stid: string,
    figmaFileId: string
): Promise<Types.FigmaSyncImportData> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/figma`;
    const body = { figmaFileKey: figmaFileId };

    return http.post(url, body).then<Types.FigmaSyncImportData>(handleResponse);
}

/**
 * Transfers ownership of styleguide with specified id to an owner with newOwnerId
 */
export function transferStyleguideOwnership(
    sid: string, newOwnerId: string, toOrganization = false
): Promise<Types.BarrelUser[]> {
    const target = toOrganization ? "organizations" : "users";
    const url = `${Zeplin.urls.baseURL}/styleguides/${sid}/${target}/${newOwnerId}/ownership`;
    return http.post(url).then<Types.BarrelUser[]>(handleResponse);
}

/**
 * Invite a user to the styleguide. If they are not a member, an invitation email will be sent out.
 */
export function inviteStyleguideUser(
    sid: string, handle?: string, role?: string, restricted?: boolean
): Promise<Types.BarrelUser> {
    const body = handle ? { handle, role, restricted } : null;
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${sid}/users`, body)
        .then<Types.BarrelUser>(handleResponse);
}

export function inviteStyleguideUsersByTag(
    oid: string, sid: string, tag: string
): Promise<Types.InviteBarrelUsersResponse> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/styleguides/${sid}/users`, { tag })
        .then<Types.InviteBarrelUsersResponse>(handleResponse);
}

/**
 * Remove a user from the project. It also means leaving a project for the Current user.
 */
export function removeUserFromStyleguide(sid: string, mid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${sid}/users/${mid}`;
    return http.delete(url).then(checkResponseError);
}

export function updateStyleguideUserRole(sid: string, mid: string, role: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${sid}/users/${mid}/role`, { role })
        .then(checkResponseError);
}

export function updateStyleguideName(sid: string, name: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${sid}/name`, { name })
        .then(checkResponseError);
}

export function updateStyleguideDescription(sid: string, description: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${sid}/description`, { description })
        .then(checkResponseError);
}

export function updateStyleguideDensityScale(sid: string, densityScale: number): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${sid}/densityscale`, { densityScale })
        .then(checkResponseError);
}

export function updateStyleguideAccessibility(sid: string, publicity: boolean): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${sid}/public`, { public: publicity })
        .then(checkResponseError);
}

export function updateStyleguideThumbnail(pid: string, thumbnail: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${pid}/thumbnail`;
    return http.put(url, { thumbnail }).then(checkResponseError);
}

export function updateStyleguidePreviewBackgroundColor(
    pid: string,
    previewBackgroundColor: RawColor | null
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${pid}/previewbackgroundcolor`;
    return http.put(url, { previewBackgroundColor }).then(checkResponseError);
}

export function getStyleguideExtensions(sid: string): Promise<Types.BarrelExtension[]> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${sid}/extensions`)
        .then<{ extensions: Types.BarrelExtension[]; }>(handleResponse)
        .then(({ extensions }) => extensions);
}

export function authStyleguideSlack(sid: string, code: string, redirectURI: string): Promise<Response> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${sid}/integrations/slacks/auth`;

    return http.post(url, { code, redirectURI })
        .then(response => {
            refreshToken(response);

            return response;
        });
}

/**
 * Get slack integration custom emoji list of the styleguide.
 * @param {String} sid Styleguide id
 */
export function getStyleguideSlackIntegrationEmojis(sid: string): Promise<Types.GetSlackIntegrationEmojisResponse> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${sid}/integrations/slacks/emojis`)
        .then<Types.GetSlackIntegrationEmojisResponse>(handleResponse);
}

/**
 * Deletes given integration from Styleguide
 * @param {String} sid Styleguide id
 * @param {String} intype type of the integration
 * @param {String} inid integration id
 */
export function deleteStyleguideIntegration(sid: string, intype: string, inid: string): Promise<void> {
    return http
        .delete(`${Zeplin.urls.baseURL}/styleguides/${sid}/integrations/${intype}/${inid}`)
        .then(checkResponseError);
}

/**
 * Add a color to the color palette of the styleguide.
 * @param {String} sid Styleguide id
 * @param {Object} color Color object
 * @param {string} requestId Request id
 */
export function createStyleguideColor(
    sid: string,
    color: Types.Color,
    requestId?: string
): Promise<Types.CreationResponse> {
    if (requestId) {
        Zeplin.headers.set("Zeplin-Request-Id", requestId);
    }

    return http.post(`${Zeplin.urls.baseURL}/styleguides/${sid}/colors`, color)
        .then<Types.CreationResponse>(handleResponse);
}

export function updateStyleguideColorsOrder(
    sid: string,
    body: Types.ColorsOrder
): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/styleguides/${sid}/colors/order`, body)
        .then(checkResponseError);
}

/**
 * Update a color in the color palette of the styleguide.
 * @param {String} sid Styleguide id
 * @param {String} cid Color id
 * @param {Object} color Color update data
 */
export function updateStyleguideColor(sid: string, cid: string, color: Partial<Types.Color>): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${sid}/colors/${cid}`, color)
        .then(checkResponseError);
}

/**
 * Update colors name in the color palette of the styleguide.
 * @param {String} sid Styleguide id
 * @param {Object} colors Color update data
 */
export function updateStyleguideColorsName(
    sid: string,
    colors: { colors: Partial<Types.Color>[]; }
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${sid}/colors/name`, colors)
        .then(checkResponseError);
}

/**
 * Delete a color from the color palette of the styleguide.
 * @param {String} sid Styleguide id
 * @param {String} cid Color id
 */
export function deleteStyleguideColor(sid: string, cid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${sid}/colors/${cid}`)
        .then(checkResponseError);
}

/**
 * Delete all colors of the styleguide.
 * @param {String} sid Styleguide id
 */
export function deleteStyleguideColors(sid: string, data: { colors: string[]; }): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${sid}/colors`, data)
        .then(checkResponseError);
}

export function createStyleguideTextStyle(
    sid: string, textStyle: Omit<Types.TextStyle, "_id">
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${sid}/textStyles`, textStyle)
        .then<Types.CreationResponse>(handleResponse);
}

export function updateStyleguideTextStyle(
    sid: string, tsid: string, textStyle: Partial<Types.TextStyle>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${sid}/textStyles/${tsid}`, textStyle)
        .then(checkResponseError);
}

export function deleteStyleguideTextStyle(pid: string, tsid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${pid}/textStyles/${tsid}`)
        .then(checkResponseError);
}

export function deleteStyleguideAllTextStyles(sid: string, body: { textStyles: string[]; }): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${sid}/textStyles`, body)
        .then(checkResponseError);
}

export function createStyleguideSpacingSection(
    stid: string, spacingSection: SpacingSectionParameters
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${stid}/spacingsections`, spacingSection)
        .then<Types.CreationResponse>(handleResponse);
}

export function updateStyleguideSpacingSection(
    stid: string,
    ssid: string,
    updatedSpacingSection: UpdateSpacingSectionParameters
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/spacingsections/${ssid}`, updatedSpacingSection)
        .then(checkResponseError);
}

export function deleteStyleguideSpacingSection(stid: string, ssid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/spacingsections/${ssid}`)
        .then(checkResponseError);
}

export function createStyleguideSpacingToken(
    stid: string, ssid: string, spacingToken: SpacingTokenParameters
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${stid}/spacingsections/${ssid}/spacingtokens`, spacingToken)
        .then<Types.CreationResponse>(handleResponse);
}

export function updateStyleguideSpacingToken(
    stid: string,
    ssid: string,
    sptid: string,
    updatedSpacingToken: Partial<SpacingTokenParameters>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/spacingsections/${ssid}/spacingtokens/${sptid}`, updatedSpacingToken)
        .then(checkResponseError);
}

export function deleteStyleguideSpacingToken(stid: string, ssid: string, sptid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/spacingsections/${ssid}/spacingtokens/${sptid}`)
        .then(checkResponseError);
}

export function updateStyleguideRemPreferences(stid: string, updatedRemPreferences: Partial<Types.RemPreferences>):
    Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/rempreferences`, updatedRemPreferences)
        .then(checkResponseError);
}

export function addStyleguideComponentPage(
    stid: string, body: Types.CreateComponentPageParameters
): Promise<Types.CreationResponse> {
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages`, body)
        .then<Types.CreationResponse>(handleResponse);
}

export function updateStyleguideComponentPage(
    stid: string, pgid: string, body: Types.UpdateComponentParameters
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages/${pgid}`, body)
        .then(checkResponseError);
}

export function deleteStyleguideComponentPages(stid: string, pgids: string[], deleteContents: boolean): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages`, { pgids, deleteContents })
        .then(checkResponseError);
}

export function deleteStyleguideComponentSectionsFromPage(
    stid: string, pgid: string, csids: string[]
): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages/${pgid}/componentsections`, { csids })
        .then(checkResponseError);
}

export function transferStyleguidePages(
    stid: string, body: Types.TransferPagesParameters
): Promise<Types.MergePagesResponse> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages/transfer`, body)
        .then<Types.MergePagesResponse>(handleResponse);
}

export function mergeStyleguidePages(
    stid: string, body: Types.MergePagesParameters
): Promise<Types.MergePagesResponse> {
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages/merge`, body)
        .then<Types.MergePagesResponse>(handleResponse);
}

export function updateStyleguidePageComponentSectionsOrder(
    stid: string, pgid: string, body: Types.UpdatePageComponentSectionsOrderParameters
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages/${pgid}/componentsections/order`, body)
        .then(checkResponseError);
}

export function updateStyleguidePagesStyleguide(
    stid: string, tstid: string, pgids: string[]
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages/styleguides/${tstid}`, { pgids })
        .then(checkResponseError);
}

export function addStyleguideComponentSection(
    stid: string, csids: string[] | undefined, pgid: string | undefined, body: Types.CreateComponentSectionParameters
): Promise<CreateComponentSectionResponse> {
    if (pgid) {
        return http
            .post(`${Zeplin.urls.baseURL}/styleguides/${stid}/pages/${pgid}/componentsections`, body)
            .then<CreateComponentSectionResponse>(handleResponse);
    }

    return http
        .post(
            `${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections${
                csids?.length ? `/${csids.join("/")}/componentsections` : ""
            }`,
            body
        )
        .then<CreateComponentSectionResponse>(handleResponse);
}

/**
 * Update component section's name or description.
 *
 * @param {String} sid Styleguide id
 * @param {String[]} csids List of section ids identifying the component section
 * @param {Object} data Section data (name or description)
 */
export function updateStyleguideComponentSection(pid: string, csids: string[], data: Types.UpdateComponentParameters):
    Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/styleguides/${pid}/componentsections/${csids.join("/")}`, data)
        .then(checkResponseError);
}

/**
 * Merge component sections:
 * - Moves source section's components to target section
 * - Deletes source section
 *
 * @param {String} sid Styleguide id
 * @param {String[]} csids List of section ids identifying the source component section
 * @param {String[]} tcsids List of section ids identifying the target component section
 * @param {boolean} flatten Should flatten inner sections
 */
export function mergeStyleguideComponentSection(sid: string, csids: string[], tcsids: string[], flatten: boolean):
    Promise<void> {
    const componentSections = [{
        csid: csids.join("/"),
        tcsid: tcsids.join("/"),
        flatten
    }];

    return http
        .put(`${Zeplin.urls.baseURL}/styleguides/${sid}/componentsections/merge`, { componentSections })
        .then(checkResponseError);
}

export function convertStyleguideComponentSectionToVariantSection(
    stid: string,
    csid: string
): Promise<Types.UpdateComponentVariantDataResponse> {
    return http
        .post(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csid}/variant`)
        .then<Types.UpdateComponentVariantDataResponse>(handleResponse);
}

export function convertStyleguideVariantSectionToComponentSection(stid: string, csid: string): Promise<void> {
    return http
        .delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csid}/variant`)
        .then(checkResponseError);
}

export function createStyleguideComponentSectionVariantProperty(
    stid: string,
    csid: string,
    name: string,
    values?: string[]
): Promise<Types.CreationResponse> {
    return http
        .post(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csid}/variant/properties`, { name, values })
        .then<Types.CreationResponse>(handleResponse);
}

export function updateStyleguideComponentSectionVariantProperty(
    stid: string,
    csid: string,
    vpid: string,
    data: Types.UpdateComponentSectionVariantParameters
): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csid}/variant/properties/${vpid}`, data)
        .then(checkResponseError);
}

export function updateStyleguideComponentSectionVariantPropertiesOrder(
    stid: string,
    csid: string,
    data: Types.UpdateComponentSectionVariantPropertiesOrder
): Promise<void> {
    return http
        .post(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csid}/variant/properties/order`, data)
        .then(checkResponseError);
}

// eslint-disable-next-line max-params
export function updateStyleguideComponentsVariantProperty(
    stid: string,
    csid: string,
    vpid: string,
    components: string[],
    value: string
): Promise<Types.UpdateComponentVariantDataResponse> {
    return http
        .put(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csid}/components/variant/properties/${vpid}`, { components, value })
        .then<Types.UpdateComponentVariantDataResponse>(handleResponse);
}

export function deleteStyleguideComponentSectionVariantProperty(
    stid: string,
    csid: string,
    vpid: string
): Promise<void> {
    return http
        .delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csid}/variant/properties/${vpid}`)
        .then(checkResponseError);
}

export function createStyleguideComponentSectionVariantGroups(
    stid: string,
    csid: string,
    propertyId: string
): Promise<Partial<ComponentSection>> {
    return http
        .post(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csid}/variant/groups`, { propertyId })
        .then<Partial<ComponentSection>>(handleResponse);
}

export function updateStyleguideComponent(
    sid: string, csids: string[], coid: string, data: Types.UpdateComponentParameters
): Promise<void> {
    return http
        .put(`${Zeplin.urls.baseURL}/styleguides/${sid}/componentsections/${csids.join("/")}/components/${coid}`, data)
        .then(checkResponseError);
}

export function deleteStyleguideComponent(
    stid: string,
    csids: string[],
    coid: string
): Promise<UpdateComponentVariantSectionResponse> {
    return http
        .delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csids.join("/")}/components/${coid}`)
        .then<UpdateComponentVariantSectionResponse>(handleResponse);
}

export function deleteStyleguideComponents(stid: string, sectionComponents: Types.SectionComponents[]):
    Promise<UpdateComponentVariantSectionsResponse> {
    return http
        .delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/components`, { components: sectionComponents })
        .then<UpdateComponentVariantSectionsResponse>(handleResponse);
}

export function deleteStyleguideComponentSection(
    stid: string,
    csids: string[]
): Promise<void> {
    return http
        .delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csids.join("/")}`)
        .then<void>(checkResponseError);
}

export function updateStyleguideParent(stid: string, parent: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/parent`, { parent })
        .then(checkResponseError);
}

export function removeStyleguideParent(stid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/styleguides/${stid}/parent`;
    return http.delete(url).then(checkResponseError);
}

export function getStyleguideIntegrationEvents(
    stid: string, inid: string
): Promise<Types.SlackPreferencesEventsResponse> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/slacks/${inid}/events`)
        .then<Types.SlackPreferencesEventsResponse>(handleResponse);
}

export function addStyleguideIntegrationEvent(stid: string, inid: string, event: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/slacks/${inid}/events/${event}`)
        .then(checkResponseError);
}

export function deleteStyleguideIntegrationEvent(stid: string, inid: string, event: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/slacks/${inid}/events/${event}`)
        .then(checkResponseError);
}

export function getStyleguideTeamsIntegration(stid: string): Promise<Types.GetTeamsIntegrationResponse> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${stid}/integrations/microsoftTeams`)
        .then<Types.GetTeamsIntegrationResponse>(handleResponse);
}

export function updateStyleguideComponentsOrder(stid: string, body: Types.UpdateComponentsOrderParameters):
    Promise<UpdateComponentVariantSectionsResponse> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/components/order`, body)
        .then<UpdateComponentVariantSectionsResponse>(handleResponse);
}

export function updateStyleguideComponentSectionsOrder(
    stid: string,
    body: Types.UpdateComponentSectionsOrderParameters
): Promise<UpdateComponentVariantSectionsResponse> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/order`, body)
        .then<UpdateComponentVariantSectionsResponse>(handleResponse);
}

export function connectStyleguideToStorybook(stid: string, url: string) {
    return http.put(`${Zeplin.urls.baseURL}/v2/styleguides/${stid}/connectedcomponents`, {
        items: [],
        metadata: {
            storybook: { url }
        }
    }).then(checkResponseError);
}

export function updateStyleguideStorybookUrl(stid: string, url: string) {
    return http.put(`${Zeplin.urls.baseURL}/v2/styleguides/${stid}/connectedcomponents/metadata/storybook`, { url }).then(checkResponseError);
}

export function deleteStyleguideConnectedComponents(stid: string) {
    return http.delete(`${Zeplin.urls.baseURL}/v2/styleguides/${stid}/connectedcomponents`).then(checkResponseError);
}

export function createStyleguideConnectedComponentItem(
    stid: string,
    body: Types.CreateConnectedComponentItemParameters
): Promise<ConnectedComponent> {
    return http.post(`${Zeplin.urls.baseURL}/v2/styleguides/${stid}/connectedcomponents/items`, body)
        .then<ConnectedComponent>(handleResponse);
}

export function deleteStyleguideConnectedComponentItem(stid: string, itemId: string) {
    return http.delete(`${Zeplin.urls.baseURL}/v2/styleguides/${stid}/connectedcomponents/items/${itemId}`).then(checkResponseError);
}

export function getStyleguideConnectedComponents(
    stid: string,
    { projectId, styleguideId }: { projectId?: string; styleguideId?: string; } = {}
): Promise<Types.GetConnectedComponentsResponse> {
    const headers = new Headers(Zeplin.headers);

    if (projectId) {
        headers.append("Zeplin-Project-Id", projectId);
    } else if (styleguideId) {
        headers.append("Zeplin-Styleguide-Id", styleguideId);
    }

    return http.get(`${Zeplin.urls.baseURL}/v2/styleguides/${stid}/connectedcomponents`, { headers })
        .then<Types.GetConnectedComponentsResponse>(handleResponse)
        .catch(() => ({ items: [], metadata: {} }));
}

export function getStyleguideLocalConnectedComponents(
    stid: string
): Promise<Types.GetLocalConnectedComponentsResponse> {
    // TODO: [SB] Use v2 endpoint
    return fetch(`http://127.0.0.1:9756/styleguides/${stid}/connectedcomponents`)
        .then(response => {
            if (response.ok) {
                return response.json().then(resJson => resJson.connectedComponents);
            }

            return null;
        })
        .then<Types.GetLocalConnectedComponentsResponse>(localConnectedComponents => ({ localConnectedComponents }))
        .catch<Types.GetLocalConnectedComponentsResponse>(() => ({ localConnectedComponents: null }));
}

export function updateStyleguidesWorkflowStatus(styleguides: string[], wsid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/workflowStatus`, { styleguides, wsid })
        .then(checkResponseError);
}

export function deleteStyleguidesWorkflowStatus(styleguides: string[]): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/workflowStatus`, { styleguides })
        .then(checkResponseError);
}

export function getStyleguideDesignTokens(
    sid: string,
    includeLinkedStyleguides: boolean
): Promise<Types.GetBarrelDesignTokensResponse> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${sid}/designTokens?includeLinkedStyleguides=${includeLinkedStyleguides}`)
        .then<Types.GetBarrelDesignTokensResponse>(handleResponse);
}

export function getStyleguideDesignTokensFile(
    sid: string,
    includeLinkedStyleguides: boolean
): Promise<{ blob: Blob; name: string; }> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${sid}/designTokens/download?includeLinkedStyleguides=${includeLinkedStyleguides}`)
        .then(handleResponseBlob);
}

export function getStyleguideComponentVersionAssets(
    stid: string, csids: string[], coid: string, vid: string
): Promise<Types.GetAssetsResponse | null> {
    return http.get(`${Zeplin.urls.baseURL}/v2/styleguides/${stid}/componentsections/${csids.join("/")}/components/${coid}/versions/${vid}/assets`)
        .then<Types.GetAssetsResponse | null>(handleResponseSilently);
}

// eslint-disable-next-line max-params
export function processStyleguideComponentVersionAssetContent(
    stid: string,
    csids: string[],
    coid: string,
    vid: string,
    vaid: string,
    ovacid: string
): Promise<void> {
    return http.post(
        `${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${csids.join("/")}/components/${coid}/versions/${vid}/assets/${vaid}/contents/${ovacid}/process`
    ).then(checkResponseError);
}

// MARK: User endpoints
export function getUserProfile(): Promise<Types.UserProfile> {
    return http.get(`${Zeplin.urls.baseURL}/v3/users`)
        .then<Types.UserProfile>(handleResponse);
}

export function getUserFreeTrialEligibility(): Promise<{
    eligibleForFreeTrial: boolean;
    previouslyUsedPaidPlan: boolean;
}> {
    return http.get(`${Zeplin.urls.baseURL}/users/freeTrial/eligibility`)
        .then<{ eligibleForFreeTrial: boolean; previouslyUsedPaidPlan: boolean; }>(handleResponse);
}

type MFASettingResponse = {
    mfa: {
        enabled: boolean;
        required: boolean;
        sessionDuration: string;
    };
};
export function setupMFAAdminSettings(
    oid: string,
    body: {
        required: boolean;
        sessionDuration: string;
    }
): Promise<MFASettingResponse> {
    return http.post(`${Zeplin.urls.baseURL}/organizations/${oid}/mfa`, body)
        .then<MFASettingResponse>(handleResponse);
}

export function updateMFAAdminSettings(
    oid: string,
    body: {
        enabled: boolean;
        required: boolean;
        sessionDuration: string;
    }
): Promise<MFASettingResponse> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/mfa`, body)
        .then<MFASettingResponse>(handleResponse);
}

export function getUserMFASetupData(): Promise<Types.MFASetupData> {
    return http.post(`${Zeplin.urls.baseURL}/users/mfa`)
        .then<Types.MFASetupData>(handleResponse);
}

export function getIndividualUserMFASetupData(): Promise<Types.MFASetupData> {
    return http.post(`${Zeplin.urls.baseURL}/users/mfa/setup`)
        .then<Types.MFASetupData>(handleResponse);
}

type VerifyUserMFASetupResponse = { token: string; recoveryCodes: string[]; };
export function verifyUserMFASetup(code: string): Promise<VerifyUserMFASetupResponse> {
    return http.post(`${Zeplin.urls.baseURL}/users/mfa/verify`, { code })
        .then<VerifyUserMFASetupResponse>(handleResponse);
}

export function verifyIndividualUserMFASetup(code: string): Promise<VerifyUserMFASetupResponse> {
    return http.post(`${Zeplin.urls.baseURL}/users/mfa/setup/verify`, { code })
        .then<VerifyUserMFASetupResponse>(handleResponse);
}

type ResetUserMFASetupResponse = { token: string; };
export function resetUserMFASetup(): Promise<ResetUserMFASetupResponse> {
    return http.post(`${Zeplin.urls.baseURL}/users/mfa/reset`)
        .then<ResetUserMFASetupResponse>(handleResponse);
}

export function resetIndividualUserMFASetup(): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/users/mfa/reset`)
        .then(checkResponseError);
}

export function removeIndividualUserMFASetup(): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/users/mfa/setup`)
        .then(checkResponseError);
}

export function getMFAAvailability(): Promise<MFAAvailability> {
    return http.get(`${Zeplin.urls.baseURL}/users/mfa/availability`)
        .then<MFAAvailability>(handleResponse);
}

type RecoverUserMFASetupResponse = { token: string; };
export function recoverUserMFASetup(code: string): Promise<RecoverUserMFASetupResponse> {
    return http.post(`${Zeplin.urls.baseURL}/users/mfa/recover`, { code })
        .then<RecoverUserMFASetupResponse>(handleResponse);
}

type UserStatusResponse = { status: "invited" | "registered" | "verified"; };
export function getUserStatus(email: string): Promise<UserStatusResponse> {
    return http.get(`${Zeplin.urls.baseURL}/users/${email}/status`)
        .then<UserStatusResponse>(handleResponse);
}

export function checkUsernameAvailable(username: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/users/${username}/available`).then(checkResponseError);
}

export function updateUsername(username: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/username`, { username }).then(checkResponseError);
}

type UserTokenResponse = { _id: string; token: string; };
export function updateEmail(body: { email: string; password: string; }): Promise<UserTokenResponse> {
    return http.put(`${Zeplin.urls.baseURL}/users/email`, body)
        .then<UserTokenResponse>(handleResponse);
}

type UpdateUserBody = { fullName?: string; bio?: string; profession?: string; designTool?: string; };
export function updateUser(body: UpdateUserBody): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users`, body)
        .then(checkResponseError);
}

export function updateUserTrackingPreference(value: boolean): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/privacy/trackingOptOut`, { value })
        .then(checkResponseError);
}

export function deleteUser(password: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/users/delete`, { password })
        .then(checkResponseError);
}

export function updatePassword(
    body: { oldPassword: string; newPassword: string; }
): Promise<UserTokenResponse> {
    return http.put(`${Zeplin.urls.baseURL}/users/password`, body)
        .then<UserTokenResponse>(handleResponse);
}

export function getUserBilling(): Promise<Types.GetBillingResponse> {
    return http.get(`${Zeplin.urls.baseURL}/users/billing`).then<Types.GetBillingResponse>(handleResponse);
}

export function updateUserBilling(body: Types.Billing): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/billing`, body)
        .then(checkResponseError);
}

export function getUserCards(): Promise<Types.Card[]> {
    return http.get(`${Zeplin.urls.baseURL}/stripe/users/cards`)
        .then<{ cards: Types.Card[]; }>(handleResponse)
        .then(({ cards }) => cards);
}

export function updateUserCard(stripeToken: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/stripe/users/cards`, { stripeToken })
        .then(checkResponseError);
}

export function requestInvoice(invoiceId: string, oid?: string): Promise<void> {
    let url;
    if (oid) {
        url = `${Zeplin.urls.baseURL}/stripe/organizations/${oid}/invoices/${invoiceId}/send`;
    } else {
        url = `${Zeplin.urls.baseURL}/stripe/users/invoices/${invoiceId}/send`;
    }

    return http.post(url).then(checkResponseError);
}

export function downloadInvoice(invoiceId: string, oid?: string): Promise<{ blob: Blob; name: string; }> {
    let url;
    if (oid) {
        url = `${Zeplin.urls.baseURL}/stripe/organizations/${oid}/invoices/${invoiceId}/download`;
    } else {
        url = `${Zeplin.urls.baseURL}/stripe/users/invoices/${invoiceId}/download`;
    }

    return http.post(url).then(handleResponseBlob);
}

export function getUserInvoices(options: Types.GetInvoicesParameters): Promise<Types.GetInvoicesResponse> {
    const url = new URL(`${Zeplin.urls.baseURL}/stripe/users/invoices`);
    url.search = new URLSearchParams(options as Record<string, string>).toString();

    return http.get(url.toString()).then<Types.GetInvoicesResponse>(handleResponse);
}

export function requestVerification(): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/users/requests/verification`)
        .then(checkResponseError);
}

export function verifyUser(): Promise<Types.VerifyUserResponse> {
    const url = `${Zeplin.urls.baseURL}/users/verify`;
    return http.post(url).then<Types.VerifyUserResponse>(handleResponse);
}

export function loginUser(handle: string, password: string): Promise<Types.UserProfileWithToken> {
    return http.post(`${Zeplin.urls.baseURL}/v2/users/login`, { handle, password })
        .then<Types.UserProfileWithToken>(handleResponse);
}

type CreateUserResponse = {
    _id: string;
    token: string;
    sso: Types.UserSSO[];
    privacyPreferences: {
        trackingOptOut: {
            value: boolean;
        };
    };
    accountCaptureInfo?: Types.AccountCaptureInfo;
    ssoLoginParams?: Types.SSOLoginParams;
    mfaSetupRequired?: boolean;
};

// TODO: Does passing headers make sense?
// Should we override http module to take optional headers?
export function createUser(credentials: Types.UserCredentials, headers: HeadersInit): Promise<CreateUserResponse> {
    return fetch(`${Zeplin.urls.baseURL}/users`, {
        method: "POST",
        headers,
        body: JSON.stringify(credentials)
    }).then<CreateUserResponse>(handleResponse);
}

export function getTerms(): Promise<Types.Terms[]> {
    return http.get(`${Zeplin.urls.baseURL}/users/terms?status=waiting_for_approval`)
        .then<Types.Terms[]>(handleResponse);
}

export function acceptTerms(teid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/terms/${teid}/accept`).then(checkResponseError);
}

type CreateAuthGrantCredentialsResponse = { id: string; };
export function createAuthGrantCredentials(): Promise<CreateAuthGrantCredentialsResponse> {
    return fetch(`${Zeplin.urls.baseURL}/auth/grant`, {
        method: "POST"
    }).then<CreateAuthGrantCredentialsResponse>(handleResponse);
}

type CreateAuthGrantCodeResponse = { code: string; };
export function createAuthGrantCode(redeemerId: string, token: string): Promise<CreateAuthGrantCodeResponse> {
    const headers = new Headers();
    headers.set("Content-Type", "application/json");
    headers.set("Zeplin-Token", token);
    const body = JSON.stringify({
        redeemerId
    });
    return fetch(`${Zeplin.urls.baseURL}/auth/grant/code`, {
        method: "POST",
        headers,
        body
    }).then<CreateAuthGrantCodeResponse>(handleResponse);
}

type GetLinkedTokenOfAuthKeyResponse = { token: string; user?: Types.UserProfile; };
export async function getLinkedTokenOfAuthKey(clientId: string, authKey: string)
    : Promise<GetLinkedTokenOfAuthKeyResponse> {
    const query = `client_id=${clientId}&auth_key=${authKey}`;

    const response = await http.get(`${BASE_URL}/oauth/linkedToken?${query}`);
    return handleResponse<GetLinkedTokenOfAuthKeyResponse>(response);
}

export function revokeAppToken(clientId: string): Promise<void> {
    // eslint-disable-next-line camelcase
    return http.delete(`${Zeplin.urls.baseURL}/oauth/token`, { client_id: clientId })
        .then(checkResponseError);
}

/**
 * Move colors to target styleguide from project
 *
 * @param {String} pid project id
 * @param {String} tstid target styleguide id
 */
export function updateProjectColorsStyleguide(pid: string, tstid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/colors/styleguides/${tstid}`).then(checkResponseError);
}

/**
 * Move text styles to target styleguide from project
 */
export function updateProjectTextStylesStyleguide(pid: string, tstid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/textstyles/styleguides/${tstid}`).then(checkResponseError);
}

/**
 * Move component sections to target styleguide from project
 *
 * @param {String} pid project id
 * @param {String} tstid target styleguide id
 * @param {String[]} csids component section ids to move
 */
export function updateProjectComponentSectionsStyleguide(pid: string, tstid: string, csids: string[]): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/styleguides/${tstid}`, { componentSections: csids }).then(checkResponseError);
}

/**
 * Move colors to target styleguide from styleguide
 *
 * @param {String} stid styleguide id
 * @param {String} tstid target styleguide id
 */
export function updateStyleguideColorsStyleguide(stid: string, tstid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/colors/styleguides/${tstid}`).then(checkResponseError);
}

/**
 * Move text styles to target styleguide from styleguide
 */
export function updateStyleguideTextStylesStyleguide(stid: string, tstid: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/textstyles/styleguides/${tstid}`).then(checkResponseError);
}

/**
 * Move component sections to target styleguide from styleguide
 *
 * @param {String} stid styleguide id
 * @param {String} tstid target styleguide id
 * @param {String[]} csids component section ids to move
 */
export function updateStyleguideComponentSectionsStyleguide(stid: string, tstid: string, csids: string[]):
    Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/styleguides/${tstid}`, { componentSections: csids }).then(checkResponseError);
}

// MARK: Public API endpoints

/**
 * Get authorized applications list of the user.
 */
export function getAuthorizedApplications(): Promise<Types.OauthApplication[]> {
    return http.get(`${Zeplin.urls.baseURL}/users/authorizedApplications`)
        .then<{ applications: Types.OauthApplication[]; }>(handleResponse)
        .then(resJson => resJson.applications);
}

/**
 * Revoke authorized application.
 */
export function revokeAuthorizedApplication(appId: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/users/authorizedApplications/${appId}`).then(checkResponseError);
}

/**
 * Get owned applications list of the user.
 */
export function getOwnedApplications(): Promise<Types.OwnedApplication[]> {
    return http.get(`${Zeplin.urls.baseURL}/users/applications`)
        .then<{ applications: Types.OwnedApplication[]; }>(handleResponse)
        .then(resJson => resJson.applications);
}

/**
 * Get owned application.
 */
export function getOwnedApplication(appId: string): Promise<Types.OwnedApplicationDetails> {
    return http.get(`${Zeplin.urls.baseURL}/users/applications/${appId}`).then<Types.OwnedApplicationDetails>(handleResponse);
}

/**
 * Create an application.
 */
export function createApplication(
    values: Types.OwnedApplicationRequestParameters
): Promise<Types.CreatedOwnedApplication> {
    return http.post(`${Zeplin.urls.baseURL}/users/applications`, values).then<Types.CreatedOwnedApplication>(handleResponse);
}

/**
 * Update an application.
 */
export function updateApplication(appId: string, values: Types.OwnedApplicationRequestParameters): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/applications/${appId}`, values).then(checkResponseError);
}

/**
 * Delete an application.
 */
export function deleteApplication(appId: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/users/applications/${appId}`).then(checkResponseError);
}

/**
 * Get personal access tokens list of the user.
 */
export function getPersonalAccessTokens(): Promise<Types.PersonalAccessToken[]> {
    return http.get(`${Zeplin.urls.baseURL}/users/personalAccessTokens`)
        .then<{ personalAccessTokens: Types.PersonalAccessToken[];}>(handleResponse)
        .then(({ personalAccessTokens }) => personalAccessTokens);
}

export function createPersonalAccessToken(
    { name, hasAdminAccess }: { name: string; hasAdminAccess: boolean; }
): Promise<Types.PersonalAccessToken> {
    const body: { name: string; requiredScopes?: string[]; } = { name };

    if (hasAdminAccess) {
        body.requiredScopes = ["admin"];
    }

    return http.post(`${Zeplin.urls.baseURL}/users/personalAccessTokens`, body)
        .then<Types.PersonalAccessToken>(handleResponse);
}

export function deletePersonalAccessToken(tokenId: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/users/personalAccessTokens/${tokenId}`).then(checkResponseError);
}

/**
 * Rename a personal access token.
 */
export function renamePersonalAccessToken(tokenId: string, name: string): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/personalAccessTokens/${tokenId}`, { name }).then(checkResponseError);
}

/**
 * Get application connect status
 */
export function getAppStatus(clientId: string): Promise<boolean> {
    return http.head(`${Zeplin.urls.baseURL}/oauth/token?client_id=${clientId}`)
        .then(res => res.headers.get("Oauth-Token-Exists") === "true");
}

/**
 * Get webhooks
 * @param {String} oid organization id
 */
export function getWebhooks(oid: string): Promise<Types.Webhook[]> {
    const url = oid && oid !== "user"
        ? `${Zeplin.urls.baseURL}/organizations/${oid}/webhooks`
        : `${Zeplin.urls.baseURL}/users/webhooks`;

    return http.get(url)
        .then<{ webhooks: Types.Webhook[]; }>(handleResponse)
        .then(response => response.webhooks);
}

/**
 * Get webhook
 * @param {String} oid organization id
 * @param {String} webhookId webhook id
 */
export function getWebhook(oid: string, webhookId: string): Promise<Types.WebhookDetails> {
    const url = oid && oid !== "user"
        ? `${Zeplin.urls.baseURL}/organizations/${oid}/webhooks/${webhookId}`
        : `${Zeplin.urls.baseURL}/users/webhooks/${webhookId}`;

    return http.get(url).then<Types.WebhookDetails>(handleResponse);
}

/**
 * Create webhook
 * @param {String} oid organization id
 * @param webhook values
 */
export function createWebhook(
    oid: string, webhook: Types.CreateWebhookParameters
): Promise<Types.CreateWebhookResponse> {
    const url = oid && oid !== "user"
        ? `${Zeplin.urls.baseURL}/organizations/${oid}/webhooks`
        : `${Zeplin.urls.baseURL}/users/webhooks`;

    return http.post(url, webhook).then<Types.CreateWebhookResponse>(handleResponse);
}

/**
 * Update webhook
 * @param {String} oid organization id
 * @param {String} webhookId webhook id
 * @param {Object} webhook values
 */
export function updateWebhook(oid: string, webhookId: string, webhook: Types.UpdateWebhookParameters): Promise<void> {
    const url = oid && oid !== "user"
        ? `${Zeplin.urls.baseURL}/organizations/${oid}/webhooks/${webhookId}`
        : `${Zeplin.urls.baseURL}/users/webhooks/${webhookId}`;

    return http.put(url, webhook).then(checkResponseError);
}

/**
 * Delete webhook
 * @param {String} oid organization id
 * @param {String} webhookId webhook id
 */
export function deleteWebhook(oid: string, webhookId: string): Promise<void> {
    const url = oid && oid !== "user"
        ? `${Zeplin.urls.baseURL}/organizations/${oid}/webhooks/${webhookId}`
        : `${Zeplin.urls.baseURL}/users/webhooks/${webhookId}`;

    return http.delete(url).then(checkResponseError);
}

/**
 * Webhook URL health check
 * @param {String} url webhook url
 */
export function checkWebhookUrlHealth(url: string): Promise<Response> {
    return http.post(`${Zeplin.urls.baseURL}/webhooks/urlhealthcheck`, { url });
}

// MARK: Barrel Import Suggestions
export function getSuggestedProjectResources(uid: string, pid: string):
    Promise<Types.GetSuggestedResourcesResponse> {
    return http.get(`${Zeplin.urls.baseURL}/users/${uid}/projects/${pid}/suggestions`)
        .then<Types.GetSuggestedResourcesResponse>(handleResponse);
}

export function getSuggestedStyleguideResources(uid: string, stid: string):
    Promise<Types.GetSuggestedResourcesResponse> {
    return http.get(`${Zeplin.urls.baseURL}/users/${uid}/styleguides/${stid}/suggestions`)
        .then<Types.GetSuggestedResourcesResponse>(handleResponse);
}

export function deleteSuggestedProjectResources(uid: string, pid: string, suggestionType: "colors" | "textStyles"):
    Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/users/${uid}/projects/${pid}/suggestions/${suggestionType}`)
        .then(checkResponseError);
}

export function deleteSuggestedStyleguideResources(uid: string, stid: string, suggestionType: "colors" | "textStyles"):
    Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/users/${uid}/styleguides/${stid}/suggestions/${suggestionType}`)
        .then(checkResponseError);
}

/**
 * Get id of all flows of the project
 * @param {String} pid project id
 */
export function getProjectFlows(pid: string): Promise<Types.Flow[]> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/flows`)
        .then<{ flows: Types.Flow[]; }>(handleResponse)
        .then(response => response.flows);
}

/**
 * Get id of all flows related to the screen
 * @param {String} pid project id
 * @param {String} sid screen id
 */
export function getScreenFlows(pid: string, sid: string): Promise<{ _id: string; }[]> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/flows`)
        .then<Types.GetProjectFlowIdsResponse>(handleResponse)
        .then(response => response.flows);
}

/**
 * Get flow of the project
 * @param {String} pid project id
 * @param {String} flid flow id
 */
export function getProjectFlow(pid: string, flid: string): Promise<Types.Flow> {
    return http.get(`${Zeplin.urls.baseURL}/v3/projects/${pid}/flows/${flid}`).then<Types.Flow>(handleResponse);
}

/**
 * Get dots of a flow
 * @param {String} pid project id
 * @param {String} flid flow id
 */
export function getProjectFlowDots(pid: string, flid: string): Promise<Types.FlowDot[]> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots`)
        .then<{ dots: Types.FlowDot[]; }>(handleResponse)
        .then<Types.FlowDot[]>(response => response.dots || []);
}

/**
 * Add dot to flow
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {FlowDot} dot dot
 */
export function createFlowDot(pid: string, flid: string, dot: Types.CreateFlowDotParameters):
    Promise<Types.FlowDot> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots`, dot)
        .then<Types.FlowDot>(handleResponse);
}

/**
 * Add comment to flow dot
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {FlowDot} dot dot
 * @param {string} note note
 */
export function createFlowDotComment(pid: string, flid: string, did: string, note: string):
    Promise<Types.FlowDotComment> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots/${did}/comments`, { note })
        .then<Types.FlowDotComment>(handleResponse);
}

/**
 * Update dot of a flow
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {FlowDot} dot dot
 */
export function updateProjectFlowDot(
    pid: string, flid: string, did: string, dot: Partial<Types.CreateFlowDotParameters>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots/${did}`, dot).then(checkResponseError);
}

/**
 * Update comment of a flow dot
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {FlowDot} dot dot
 * @param {string} note note
 */
// eslint-disable-next-line max-params
export function updateProjectFlowDotComment(
    pid: string, flid: string, did: string, cmid: string, note: string
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots/${did}/comments/${cmid}/note`, { note }).then(checkResponseError);
}

// eslint-disable-next-line max-params
export function addProjectFlowDotCommentReaction(
    pid: string, flid: string, did: string, cmid: string, reaction: string
): Promise<AddReactionResponse> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots/${did}/comments/${cmid}/reactions`;
    return http.post(url, { type: reaction }).then<AddReactionResponse>(handleResponse);
}

// eslint-disable-next-line max-params
export function removeProjectFlowDotCommentReaction(
    pid: string, flid: string, did: string, cmid: string, cmrid: string
): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots/${did}/comments/${cmid}/reactions/${cmrid}`;
    return http.delete(url).then(checkResponseError);
}

/**
 * Delete a dot of a flow.
 * @param {string} pid project id
 * @param {string} flid flow id
 * @param {string} did dot id
 */
export function deleteFlowDot(pid: string, flid: string, did: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots/${did}`;
    return http.delete(url).then(checkResponseError);
}

/**
 * Deletes a comment of a flow dot
 * @param {string} pid project id
 * @param {string} flid flow id
 * @param {string} did dot id
 * @param {string} cid comment id
 */
export function deleteFlowComment(pid: string, flid: string, did: string, cid: string): Promise<void> {
    const url = `${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/dots/${did}/comments/${cid}`;
    return http.delete(url).then(checkResponseError);
}

/**
 * Create flow for the project
 * @param {String} pid project id
 * @param {String} flow flow parameters project flow should have
 */
export function createFlow(
    pid: string, flow: { nodes?: Types.FlowNodeRequest[]; name?: string; }
): Promise<Types.Flow> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows`, flow).then<Types.Flow>(handleResponse);
}

/**
 * Add screen or variant group nodes to flow
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {Types.FlowNode} nodes screen, variant group, or text label nodes to be added
 */
export function addNodesToFlow(
    pid: string,
    flid: string,
    nodes: Types.FlowNodeRequest[],
    ignoreLinks?: boolean
): Promise<Types.AddNodesToFlowResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/nodes`, { nodes, ignoreLinks })
        .then<Types.AddNodesToFlowResponse>(handleResponse);
}

/**
 * Update specified nodes' positions
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {Types.UpdateNodePositionParameters} updatedNodes new positions of the nodes to be updated
 */
export function updateNodesPosition(
    pid: string,
    flid: string,
    nodes: Types.UpdateNodePositionParameters[],
    connectors?: Types.UpdateNodePositionConnectorParameters[]
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/nodes/position`, {
        nodes,
        connectors
    })
        .then(checkResponseError);
}

export function updateFlowNode(
    pid: string,
    flid: string,
    noid: string,
    updatedNode: Types.UpdateFlowNodeParameters
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/v2/projects/${pid}/flows/${flid}/nodes/${noid}`, updatedNode)
        .then(checkResponseError);
}

/**
 * Deletes nodes from the flow
 * @param {String} pid project id
 * @param {String} flid flow id
 */
export function deleteFlowNodes(pid: string, flid: string, nodes: string[]): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/nodes`, { nodes }).then(checkResponseError);
}

/**
 * Creates connector between two nodes
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {Omit<Types.FlowConnector, "_id">[]} connectors connectors to be created
 */
export function createFlowConnectors(
    pid: string, flid: string, connectors: Omit<Types.FlowConnector, "_id">[]
): Promise<Types.CreateConnectorsResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/connectors`, { connectors })
        .then<Types.CreateConnectorsResponse>(handleResponse);
}

/**
 * Updates connector
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {String} conid connector id
 * @param {Types.FlowConnector} updatedConnector connector with to be updated fields
 */
export function updateFlowConnector(
    pid: string,
    flid: string,
    conid: string,
    updatedConnector: RecursivePartial<Types.FlowConnector>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/connectors/${conid}`, updatedConnector)
        .then(checkResponseError);
}

/**
 * Deletes connector label
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {String} conid connector id
 * @param {Types.FlowConnectorLabel} updatedConnectorLabel connector label with to be updated fields
 */
export function updateFlowConnectorLabel(
    pid: string,
    flid: string,
    conid: string,
    updatedConnectorLabel: Partial<Types.FlowConnectorLabel>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/connectors/${conid}/label`, updatedConnectorLabel)
        .then(checkResponseError);
}

/**
 * Updates connector label
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {String} conid connector id
 */
export function deleteFlowConnectorLabel(
    pid: string,
    flid: string,
    conid: string
): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/connectors/${conid}/label`)
        .then(checkResponseError);
}

export function createFlowGroups(
    pid: string,
    flid: string,
    groups: Omit<Types.FlowGroup, "_id">[]
): Promise<Types.CreateFlowGroupsResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/groups`, { groups })
        .then<Types.CreateFlowGroupsResponse>(handleResponse);
}

export function deleteFlowGroup(
    pid: string,
    flid: string,
    fgid: string
): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/groups/${fgid}`)
        .then(checkResponseError);
}

export function addNodesToFlowGroup(
    pid: string,
    flid: string,
    fgid: string,
    nodes: string[]
): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/groups/${fgid}/nodes`, { nodes })
        .then(checkResponseError);
}

export function removeNodesFromFlowGroup(
    pid: string,
    flid: string,
    fgid: string,
    nodes: string[]
): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/groups/${fgid}/nodes`, { nodes })
        .then(checkResponseError);
}

export function updateFlowGroupColor(
    pid: string,
    flid: string,
    fgid: string,
    color: Types.Color
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/groups/${fgid}/color`, { color })
        .then(checkResponseError);
}

export function updateFlowGroupName(
    pid: string,
    flid: string,
    fgid: string,
    name: string
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/groups/${fgid}/name`, { name })
        .then(checkResponseError);
}

export function updateFlowGroupsOrder(
    pid: string,
    flid: string,
    groups: string[],
    index: number
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/groups/order`, { groups, index })
        .then(checkResponseError);
}

export function transferGroupsToAnotherFlow(
    pid: string,
    flid: string,
    targetFlid: string,
    groups: string[]
): Promise<Types.TransferFlowGroupsResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/groups/transfer`, { groups, targetFlid })
        .then<Types.TransferFlowGroupsResponse>(handleResponse);
}

export function transferNodesToAnotherFlow(
    pid: string,
    flid: string,
    targetFlid: string,
    nodes: string[]
): Promise<Types.TransferFlowGroupsResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/nodes/transfer`, { nodes, targetFlid })
        .then<Types.TransferFlowGroupsResponse>(handleResponse);
}

export function updateFlowName(
    pid: string,
    flid: string,
    name: string
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/name`, { name })
        .then(checkResponseError);
}

export function updateFlowsOrder(
    pid: string,
    flows: string[],
    index: number
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/order`, { flows, index })
        .then(checkResponseError);
}

export function deleteFlow(
    pid: string,
    flid: string
): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}`)
        .then(checkResponseError);
}

/**
 * Deletes connector
 * @param {String} pid project id
 * @param {String} flid flow id
 * @param {String} conid connector id
 */
export function deleteFlowConnectors(pid: string, flid: string, connectors: string[]): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/connectors`, {
        connectors
    }).then(checkResponseError);
}

type SwapPlaceholderWithScreenBody = {
    type: FlowNodeType.Screen;
    screen: string;
} | {
    type: FlowNodeType.VariantGroup;
    defaultVariant: string;
    variantGroup: string;
};
export function swapPlaceholderWithScreen(
    pid: string,
    flid: string,
    noid: string,
    body: SwapPlaceholderWithScreenBody
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/flows/${flid}/nodes/${noid}/swap`, body)
        .then(checkResponseError);
}

// MARK: Settings

export function updateUseDesktopAppForLinksInSettings(
    useDesktopAppForLinks: boolean
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/users/settings/useDesktopAppForLinks`, { useDesktopAppForLinks })
        .then(checkResponseError);
}

export function updateAIFeaturesInSettings(
    oid: string,
    aiFeaturesDisabled: boolean
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/organizations/${oid}/settings/aiFeaturesDisabled`, { aiFeaturesDisabled })
        .then(checkResponseError);
}

// MARK: NPS Endpoints

export function getNPSUserData(): Promise<Types.GetNPSUserDataResponse> {
    return http.get(`${Zeplin.urls.baseURL}/users/surveys/delighted/info`)
        .then<Types.GetNPSUserDataResponse>(handleResponse);
}

export function updateNPSUserData() {
    return http.put(`${Zeplin.urls.baseURL}/users/surveys/delighted/info`)
        .then(checkResponseError);
}

export function sendNPSResponse(body: Types.SendNPSResponseBody) {
    if (body.oid === "user") {
        delete body.oid;
    }

    return http.post(`${Zeplin.urls.baseURL}/users/surveys/delighted/response`, body).then(checkResponseError);
}

export function getScreenVirtualComponents(pid: string, sid: string, vid: string): Promise<Types.VirtualComponent[]> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/screens/${sid}/versions/${vid}/virtualComponents`)
        .then<{ virtualComponents: Types.VirtualComponent[]; }>(handleResponse)
        .then(({ virtualComponents }) => virtualComponents);
}

export function getProjectVirtualComponentsStats(
    pid: string,
    sourceIds: string[]
): Promise<Types.VirtualComponentStats[]> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/virtualComponents/stats`, { sourceIds })
        .then<{ virtualComponents: Types.VirtualComponentStats[]; }>(handleResponse)
        .then(({ virtualComponents }) => virtualComponents);
}

export function getProjectComponentsStats(pid: string, componentIds: string[]): Promise<Types.ComponentStats[]> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/components/stats`, { componentIds })
        .then<{ components: Types.ComponentStats[]; }>(handleResponse)
        .then(({ components }) => components);
}

export function getStyleguideComponentsStats(stid: string, componentIds: string[]): Promise<Types.ComponentStats[]> {
    return http.post(`${Zeplin.urls.baseURL}/styleguides/${stid}/components/stats`, { componentIds })
        .then<{ components: Types.ComponentStats[]; }>(handleResponse)
        .then(({ components }) => components);
}

export function getVirtualComponentDetail(pid: string, sourceId: string): Promise<Types.VirtualComponentDetail> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/virtualComponents/${sourceId}`)
        .then<Types.VirtualComponentDetail>(handleResponse);
}

export function getGlobalStyleguideComponentDetail({
    pid, stid, linkedStid, csids, coid
}: { pid?: string; stid: string; linkedStid: string; csids: string[]; coid: string; }): Promise<Types.ComponentDetail> {
    const headers = new Headers(Zeplin.headers);

    if (pid) {
        headers.append("Zeplin-Project-Id", pid);
    }

    if (linkedStid && linkedStid !== stid) {
        headers.append("Zeplin-Styleguide-Id", linkedStid);
    }

    return http.get(`${Zeplin.urls.baseURL}/styleguides/${stid}/componentsections/${
        csids[0]
    }/components/${coid}`, { headers }).then<Types.ComponentDetail>(handleResponse);
}

export function getLocalStyleguideComponentDetail(
    pid: string, csids: string[], coid: string
): Promise<Types.ComponentDetail> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/componentsections/${
        csids[0]
    }/components/${coid}`).then<Types.ComponentDetail>(handleResponse);
}

// MARK: Global Search
export function search(
    oid: string,
    body: {
        term: string;
        types: Types.GlobalSearchResultType[];
        projectIdToBoost?: string;
        styleguideIdToBoost?: string;
        sortBy?: GlobalSearchSortType;
    },
    signal?: AbortSignal
) {
    const url = `${Zeplin.urls.baseURL}/workspaces/${oid === "user" ? "personal" : oid}/search`;
    return http.post(url, body, signal).then<Types.GlobalSearchResponse>(handleResponse);
}

// MARK: Approvals
export function getApprovals(
    queryParams: {
        oid?: string;
        pid?: string;
        sid?: string;
        review?: "by_me" | "for_me";
        status?: string;
    }
): Promise<GetApprovalsResponse> {
    const url = new URL(`${Zeplin.urls.baseURL}/v2/approvals`);

    url.search = new URLSearchParams(queryParams).toString();

    return http.get(url.toString())
        .then<GetApprovalsResponse>(handleResponse)
        .then(response => ({
            approvals: response.approvals,
            aiReviews: response.aiReviews ?? []
        }));
}

export function getProjectAITweak(pid: string, airid: string): Promise<Types.AITweak> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/tweak`)
        .then<Types.AITweak>(handleResponse);
}

export function createAIReview(
    pid: string,
    body: Types.CreateAIReviewPayload
): Promise<Types.AIReview> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews`, body)
        .then<Types.AIReview>(handleResponse);
}

export function retryAIReview(
    pid: string,
    airid: string,
    screens: string[]
): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/retry`, { screens })
        .then(checkResponseError);
}

export function createAIReviewFeedback(
    pid: string,
    airid: string,
    body: AIReviewFeedback
): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/feedback`, body)
        .then(checkResponseError);
}

// eslint-disable-next-line max-params
export function createAIReviewScreenDotRating(
    pid: string,
    airid: string,
    sid: string,
    did: string,
    rating: AIReviewDotRating
): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/screens/${sid}/dots/${did}/rating`, { rating })
        .then(checkResponseError);
}

export function deleteAIReviewScreenDotRating(
    pid: string,
    airid: string,
    sid: string,
    did: string
): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/screens/${sid}/dots/${did}/rating`)
        .then(checkResponseError);
}

// eslint-disable-next-line max-params
export function updateAIReviewScreenDot(
    pid: string,
    airid: string,
    sid: string,
    did: string,
    data: {
        status?: NoteStatus;
    }
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/screens/${sid}/dots/${did}`, data)
        .then(checkResponseError);
}

export function deleteAIReviewScreenDot(
    pid: string,
    airid: string,
    sid: string,
    did: string
): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/screens/${sid}/dots/${did}`)
        .then(checkResponseError);
}

export function createAIReviewTweakInteraction(
    pid: string,
    airid: string,
    body: { screen?: string; message?: string; }
): Promise<Types.CreateAITweakPayload> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/tweak/interactions`, {
        screen: body.screen,
        message: body.message
    }).then<Types.CreateAITweakPayload>(handleResponse);
}

export function setAITweakChatRating(
    pid: string,
    airid: string,
    lineId: string,
    rating: AIReviewDotRating
): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/tweak/interactions/${lineId}/rating`, { rating })
        .then(checkResponseError);
}

export function deleteAITweakChatRating(
    pid: string,
    airid: string,
    lineId: string
): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/tweak/interactions/${lineId}/rating`)
        .then(checkResponseError);
}

export function stopAITweak(
    pid: string,
    airid: string
): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/tweak/cancel`)
        .then(checkResponseError);
}

export function deleteAIReview(pid: string, airid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}`)
        .then(checkResponseError);
}

export function stopAIReview(pid: string, airid: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/reviews/${airid}/cancel`)
        .then(checkResponseError);
}

export function createApproval(
    pid: string,
    body: Types.CreateApprovalPayload
): Promise<Types.Approval> {
    return http.post(`${Zeplin.urls.baseURL}/v2/projects/${pid}/approvals`, body)
        .then<Types.Approval>(handleResponse);
}

export function updateApproval(
    pid: string,
    apid: string,
    body: Partial<Pick<Types.CreateApprovalPayload, "name" | "note">>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/v2/projects/${pid}/approvals/${apid}`, body)
        .then(checkResponseError);
}

export function updateApprovalReview(
    pid: string,
    apid: string,
    body: Types.UpdateApprovalReviewPayload
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/v2/projects/${pid}/approvals/${apid}/review`, body)
        .then(checkResponseError);
}

export function reRequestApprovalReview(pid: string, apid: string, aprid: string): Promise<void> {
    return http.post(`${Zeplin.urls.baseURL}/v2/projects/${pid}/approvals/${apid}/reRequest`, {
        reviewerId: aprid
    }).then(checkResponseError);
}

export function deleteApprovalReviewer(pid: string, apid: string, aprid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/v2/projects/${pid}/approvals/${apid}/reviewers/${aprid}`)
        .then(checkResponseError);
}

export function getProjectVariableCollection(pid: string, vcid: string): Promise<VariableCollection | null> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/variableCollections/${vcid}`)
        .then<VariableCollection>(handleResponse)
        .catch(() => null);
}

export function getStyleguideVariableCollection(stid: string, vcid: string): Promise<VariableCollection | null> {
    return http.get(`${Zeplin.urls.baseURL}/styleguides/${stid}/variableCollections/${vcid}`)
        .then<VariableCollection>(handleResponse)
        .catch(() => null);
}

export function updateProjectVariableCollection(
    pid: string, vcid: string, variableCollectionData: Partial<VariableCollection>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/variableCollections/${vcid}`, variableCollectionData).then(checkResponseError);
}

export function updateStyleguideVariableCollection(
    stid: string, vcid: string, variableCollectionData: Partial<VariableCollection>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/variableCollections/${vcid}`, variableCollectionData).then(checkResponseError);
}

export function deleteProjectVariableCollection(pid: string, vcid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/variableCollections/${vcid}`)
        .then(checkResponseError);
}

export function deleteStyleguideVariableCollection(stid: string, vcid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/styleguides/${stid}/variableCollections/${vcid}`)
        .then(checkResponseError);
}

export function updateProjectVariableCollectionGroup(
    pid: string, vcid: string, gid: string, variableCollectionGroupData: Partial<VariableGroup>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/variableCollections/${vcid}/groups/${gid}`, variableCollectionGroupData).then(checkResponseError);
}

export function updateStyleguideVariableCollectionGroup(
    stid: string, vcid: string, gid: string, variableCollectionGroupData: Partial<VariableGroup>
): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/styleguides/${stid}/variableCollections/${vcid}/groups/${gid}`, variableCollectionGroupData).then(checkResponseError);
}

export function updateProjectVariableOrder(
    pid: string,
    vcid: string,
    gid: string,
    variables: {
        _id: string;
        to: number;
    }[]
): Promise<void> {
    return http.put(
        `${Zeplin.urls.baseURL}/projects/${pid}/variableCollections/${vcid}/groups/${gid}/variables/order`,
        { variables }
    ).then(checkResponseError);
}

export function updateStyleguideVariableOrder(
    stid: string,
    vcid: string,
    gid: string,
    variables: {
        _id: string;
        to: number;
    }[]
): Promise<void> {
    return http.put(
        `${Zeplin.urls.baseURL}/styleguides/${stid}/variableCollections/${vcid}/groups/${gid}/variables/order`,
        { variables }
    ).then(checkResponseError);
}

export function deleteWorkspace(oid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/organizations/${oid}`)
        .then(checkResponseError);
}

export function getProjectAIOrganizeResult(pid: string): Promise<Types.ProjectAIOrganizeResult> {
    return http.get(`${Zeplin.urls.baseURL}/projects/${pid}/ai/organizeRequests`)
        .then<{ aiOrganizeRequests: Types.ProjectAIOrganizeResult[]; }>(handleResponse)
        .then(response => response.aiOrganizeRequests[0]);
}

export function createProjectAIOrganize(
    pid: string,
    body: Types.CreateProjectAIOrganizePayload
): Promise<Types.CreateProjectAIOrganizeResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/organizeRequests`, body)
        .then<Types.CreateProjectAIOrganizeResponse>(handleResponse);
}

export function applyProjectAIOrganize(
    pid: string,
    aiorid: string,
    body: Types.ApplyProjectAIOrganizePayload
): Promise<Types.ApplyProjectAIOrganizeResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/ai/organizeRequests/${aiorid}/apply`, body)
        .then<Types.ApplyProjectAIOrganizeResponse>(handleResponse);
}

export function cancelProjectAIOrganize(pid: string, aiorid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/ai/organizeRequests/${aiorid}`)
        .then(checkResponseError);
}

export function createProjectNoteType(pid: string, noteType: NoteTypeData):
    Promise<Types.CreateNoteTypeResponse> {
    return http.post(`${Zeplin.urls.baseURL}/projects/${pid}/noteTypes`, {
        name: noteType.name,
        color: noteType.color
    }).then<Types.CreateNoteTypeResponse>(handleResponse);
}

export function updateProjectNoteType(pid: string, ntid: string, update: Partial<NoteTypeData>): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/noteTypes/${ntid}`, update)
        .then(checkResponseError);
}

export function updateProjectNoteTypeOrder(pid: string, ntid: string, index: number): Promise<void> {
    return http.put(`${Zeplin.urls.baseURL}/projects/${pid}/noteTypes/${ntid}/order`, { index })
        .then(checkResponseError);
}

export function removeProjectNoteType(pid: string, ntid: string): Promise<void> {
    return http.delete(`${Zeplin.urls.baseURL}/projects/${pid}/noteTypes/${ntid}`)
        .then(checkResponseError);
}

// MARK: Exports

const projectEndpoints = {
    get: getProject,
    inviteUser: inviteProjectUser,
    inviteUsersByTag: inviteProjectUsersByTag,
    reinviteUser: reinviteProjectUser,
    activate: activateProject,
    archive: archiveProject,
    delete: deleteProject,
    copy: copyProject,
    getOrganization: getProjectOrganization,
    updateName: updateProjectName,
    updateDescription: updateProjectDescription,
    updateDensityScale: updateProjectDensityScale,
    updateThumbnail: updateProjectThumbnail,
    updatePreviewBackgroundColor: updateProjectPreviewBackgroundColor,
    updateAccessibility: updateProjectAccessibility,
    updateUserRole: updateProjectUserRole,
    transferOwnership: transferProjectOwnership,
    removeUser: removeUserFromProject,
    getSlackIntegrationEmojis: getProjectSlackIntegrationEmojis,
    deleteIntegration: deleteProjectIntegration,
    getExtensions: getProjectExtensions,
    addExtension: addExtensionToProject,
    removeExtension: removeExtensionFromProject,
    createColor: createProjectColor,
    updateColorsOrder: updateProjectColorsOrder,
    updateColor: updateProjectColor,
    updateColorsName: updateProjectColorsName,
    deleteColor: deleteProjectColor,
    deleteColors: deleteProjectColors,
    updateColorsStyleguide: updateProjectColorsStyleguide,
    createTextStyle: createProjectTextStyle,
    updateTextStyle: updateProjectTextStyle,
    deleteTextStyle: deleteProjectTextStyle,
    deleteAllTextStyles: deleteProjectAllTextStyles,
    updateTextStylesStyleguide: updateProjectTextStylesStyleguide,
    createSpacingSection: createProjectSpacingSection,
    updateSpacingSection: updateProjectSpacingSection,
    deleteSpacingSection: deleteProjectSpacingSection,
    createSpacingToken: createProjectSpacingToken,
    updateSpacingToken: updateProjectSpacingToken,
    deleteSpacingToken: deleteProjectSpacingToken,
    updateRemPreferences: updateProjectRemPreferences,
    getComponentVersions: getProjectComponentVersions,
    getComponentSections: getProjectComponentSections,
    addComponentPage: addProjectComponentPage,
    updateComponentPage: updateProjectComponentPage,
    deleteComponentPages: deleteProjectComponentPages,
    deleteComponentSectionsFromPage: deleteProjectComponentSectionsFromPage,
    transferPages: transferProjectPages,
    mergePages: mergeProjectPages,
    updatePageComponentSectionsOrder: updateProjectPageComponentSectionsOrder,
    updatePagesStyleguide: updateProjectPagesStyleguide,
    addComponentSection: addProjectComponentSection,
    updateComponentSection: updateProjectComponentSection,
    mergeComponentSection: mergeProjectComponentSection,
    convertComponentSectionToVariantSection: convertProjectComponentSectionToVariantSection,
    convertVariantSectionToComponentSection: convertProjectVariantSectionToComponentSection,
    createComponentSectionVariantProperty: createProjectComponentSectionVariantProperty,
    updateComponentSectionVariantProperty: updateProjectComponentSectionVariantProperty,
    updateComponentSectionVariantPropertiesOrder: updateProjectComponentSectionVariantPropertiesOrder,
    updateComponentsVariantProperty: updateProjectComponentsVariantProperty,
    deleteComponentSectionVariantProperty: deleteProjectComponentSectionVariantProperty,
    createComponentSectionVariantGroups: createProjectComponentSectionVariantGroups,
    updateComponent: updateProjectComponent,
    deleteComponent: deleteProjectComponent,
    deleteComponents: deleteProjectComponents,
    deleteComponentSection: deleteProjectComponentSection,
    updateComponentsOrder: updateProjectComponentsOrder,
    updateComponentSectionsOrder: updateProjectComponentSectionsOrder,
    updateComponentSectionsStyleguide: updateProjectComponentSectionsStyleguide,
    authSlack: authProjectSlack,
    getIntegrationEvents: getProjectIntegrationEvents,
    addIntegrationEvent: addProjectIntegrationEvent,
    deleteIntegrationEvent: deleteProjectIntegrationEvent,
    getTeamsIntegration: getProjectTeamsIntegration,
    connectToStorybook: connectProjectToStorybook,
    updateStorybookUrl: updateProjectStorybookUrl,
    deleteConnectedComponents: deleteProjectConnectedComponents,
    createConnectedComponentItem: createProjectConnectedComponentItem,
    deleteConnectedComponentItem: deleteProjectConnectedComponentItem,
    getConnectedComponents: getProjectConnectedComponents,
    getLocalConnectedComponents: getProjectLocalConnectedComponents,
    updateWorkflowStatus: updateProjectsWorkflowStatus,
    deleteWorkflowStatus: deleteProjectsWorkflowStatus,
    getDesignTokens: getProjectDesignTokens,
    getDesignTokensFile: getProjectDesignTokensFile,
    getComponentVersionAssets: getProjectComponentVersionAssets,
    processComponentVersionAssetContent: processProjectComponentVersionAssetContent,
    getSuggestedResources: getSuggestedProjectResources,
    deleteSuggestedResources: deleteSuggestedProjectResources,
    getVariableCollection: getProjectVariableCollection,
    updateVariableCollection: updateProjectVariableCollection,
    deleteVariableCollection: deleteProjectVariableCollection,
    updateVariableCollectionGroup: updateProjectVariableCollectionGroup,
    updateVariableOrder: updateProjectVariableOrder,
    deleteWorkspace,
    createProjectNoteType,
    updateProjectNoteType,
    updateProjectNoteTypeOrder,
    removeProjectNoteType
};

const styleguideEndpoints = {
    get: getStyleguide,
    inviteUser: inviteStyleguideUser,
    inviteUsersByTag: inviteStyleguideUsersByTag,
    activate: activateStyleguide,
    archive: archiveStyleguide,
    delete: deleteStyleguide,
    copy: copyStyleguide,
    getOrganization: getStyleguideOrganization,
    updateName: updateStyleguideName,
    updateDescription: updateStyleguideDescription,
    updateDensityScale: updateStyleguideDensityScale,
    updateThumbnail: updateStyleguideThumbnail,
    updatePreviewBackgroundColor: updateStyleguidePreviewBackgroundColor,
    updateAccessibility: updateStyleguideAccessibility,
    updateUserRole: updateStyleguideUserRole,
    transferOwnership: transferStyleguideOwnership,
    removeUser: removeUserFromStyleguide,
    getSlackIntegrationEmojis: getStyleguideSlackIntegrationEmojis,
    deleteIntegration: deleteStyleguideIntegration,
    getExtensions: getStyleguideExtensions,
    addExtension: addExtensionToStyleguide,
    removeExtension: removeExtensionFromStyleguide,
    createColor: createStyleguideColor,
    updateColorsOrder: updateStyleguideColorsOrder,
    updateColor: updateStyleguideColor,
    updateColorsName: updateStyleguideColorsName,
    deleteColor: deleteStyleguideColor,
    deleteColors: deleteStyleguideColors,
    updateColorsStyleguide: updateStyleguideColorsStyleguide,
    createTextStyle: createStyleguideTextStyle,
    updateTextStyle: updateStyleguideTextStyle,
    deleteTextStyle: deleteStyleguideTextStyle,
    deleteAllTextStyles: deleteStyleguideAllTextStyles,
    updateTextStylesStyleguide: updateStyleguideTextStylesStyleguide,
    createSpacingSection: createStyleguideSpacingSection,
    updateSpacingSection: updateStyleguideSpacingSection,
    deleteSpacingSection: deleteStyleguideSpacingSection,
    createSpacingToken: createStyleguideSpacingToken,
    updateSpacingToken: updateStyleguideSpacingToken,
    deleteSpacingToken: deleteStyleguideSpacingToken,
    updateRemPreferences: updateStyleguideRemPreferences,
    getComponentVersions: getStyleguideComponentVersions,
    addComponentPage: addStyleguideComponentPage,
    updateComponentPage: updateStyleguideComponentPage,
    deleteComponentPages: deleteStyleguideComponentPages,
    deleteComponentSectionsFromPage: deleteStyleguideComponentSectionsFromPage,
    transferPages: transferStyleguidePages,
    mergePages: mergeStyleguidePages,
    updatePageComponentSectionsOrder: updateStyleguidePageComponentSectionsOrder,
    updatePagesStyleguide: updateStyleguidePagesStyleguide,
    addComponentSection: addStyleguideComponentSection,
    updateComponentSection: updateStyleguideComponentSection,
    mergeComponentSection: mergeStyleguideComponentSection,
    convertComponentSectionToVariantSection: convertStyleguideComponentSectionToVariantSection,
    convertVariantSectionToComponentSection: convertStyleguideVariantSectionToComponentSection,
    createComponentSectionVariantProperty: createStyleguideComponentSectionVariantProperty,
    updateComponentSectionVariantProperty: updateStyleguideComponentSectionVariantProperty,
    updateComponentSectionVariantPropertiesOrder: updateStyleguideComponentSectionVariantPropertiesOrder,
    updateComponentsVariantProperty: updateStyleguideComponentsVariantProperty,
    deleteComponentSectionVariantProperty: deleteStyleguideComponentSectionVariantProperty,
    createComponentSectionVariantGroups: createStyleguideComponentSectionVariantGroups,
    updateComponent: updateStyleguideComponent,
    deleteComponent: deleteStyleguideComponent,
    deleteComponents: deleteStyleguideComponents,
    deleteComponentSection: deleteStyleguideComponentSection,
    updateComponentsOrder: updateStyleguideComponentsOrder,
    updateComponentSectionsOrder: updateStyleguideComponentSectionsOrder,
    updateComponentSectionsStyleguide: updateStyleguideComponentSectionsStyleguide,
    authSlack: authStyleguideSlack,
    getIntegrationEvents: getStyleguideIntegrationEvents,
    addIntegrationEvent: addStyleguideIntegrationEvent,
    deleteIntegrationEvent: deleteStyleguideIntegrationEvent,
    getTeamsIntegration: getStyleguideTeamsIntegration,
    connectToStorybook: connectStyleguideToStorybook,
    updateStorybookUrl: updateStyleguideStorybookUrl,
    deleteConnectedComponents: deleteStyleguideConnectedComponents,
    createConnectedComponentItem: createStyleguideConnectedComponentItem,
    deleteConnectedComponentItem: deleteStyleguideConnectedComponentItem,
    getConnectedComponents: getStyleguideConnectedComponents,
    getLocalConnectedComponents: getStyleguideLocalConnectedComponents,
    updateWorkflowStatus: updateStyleguidesWorkflowStatus,
    deleteWorkflowStatus: deleteStyleguidesWorkflowStatus,
    getDesignTokens: getStyleguideDesignTokens,
    getDesignTokensFile: getStyleguideDesignTokensFile,
    getComponentVersionAssets: getStyleguideComponentVersionAssets,
    processComponentVersionAssetContent: processStyleguideComponentVersionAssetContent,
    getSuggestedResources: getSuggestedStyleguideResources,
    deleteSuggestedResources: deleteSuggestedStyleguideResources,
    getVariableCollection: getStyleguideVariableCollection,
    updateVariableCollection: updateStyleguideVariableCollection,
    deleteVariableCollection: deleteStyleguideVariableCollection,
    updateVariableCollectionGroup: updateStyleguideVariableCollectionGroup,
    updateVariableOrder: updateStyleguideVariableOrder
};

const userEndpoints = {
    checkUsernameAvailable,
    getUserStatus,
    loginUser,
    createUser
};

const authEndpoints = {
    createAuthGrantCredentials,
    createAuthGrantCode
};

const developerEndpoints = {
    getAuthorizedApplications,
    revokeAuthorizedApplication,
    getOwnedApplications,
    getOwnedApplication,
    createApplication,
    updateApplication,
    deleteApplication,
    getPersonalAccessTokens,
    createPersonalAccessToken,
    renamePersonalAccessToken,
    deletePersonalAccessToken,
    getAppStatus,
    getWebhooks,
    getWebhook,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    checkWebhookUrlHealth
};

const flowEndpoints = {
    getProjectFlows,
    getScreenFlows,
    getProjectFlow,
    getProjectFlowDots,
    createFlowDot,
    createFlowDotComment,
    updateProjectFlowDot,
    updateProjectFlowDotComment,
    deleteFlowDot,
    deleteFlowComment,
    createFlow,
    addNodesToFlow,
    updateFlowNode,
    updateNodesPosition,
    deleteFlowNodes,
    createFlowConnectors,
    updateFlowConnector,
    deleteFlowConnectors,
    updateFlowConnectorLabel,
    deleteFlowConnectorLabel,
    createFlowGroups,
    deleteFlowGroup,
    addNodesToFlowGroup,
    removeNodesFromFlowGroup,
    updateFlowGroupColor,
    updateFlowGroupName,
    updateFlowGroupsOrder
};

const npsEndpoints = {
    getNPSUserData,
    updateNPSUserData,
    sendNPSResponse
};

export {
    projectEndpoints as project,
    styleguideEndpoints as styleguide,
    authEndpoints as auth,
    userEndpoints as user,
    developerEndpoints as developer,
    flowEndpoints as flow,
    npsEndpoints as nps
};
